import {isVideoAdSource,adSourceDownloadUrl} from '@shared/adSourceMedia';
import type {DiscoverMyAd} from '@shared/discoverMyAds';
import type { DiscoverCompetitor } from '@shared/discoverCompetitors';
import { Switch } from '@/components/ui/switch';
import { importBatchCloneSource } from './batch-clone-source';
import {cloneBatchSelection,cloneOutputSizes,cloneOutputSlots} from '@shared/cloneBatch';
import { LibraryMedia, libraryMediaRatio } from '@/components/library-media';
import { canvasRunLeft } from '@shared/canvasGeometry';
import { templateFilterTriggerStyle, templateFilterPanelStyle, templateFilterItemStyle, templateCardActionStyle } from '@/components/template-discover-styles';
import { SaveTemplateBrandMenu, TemplateBrandFilter } from '@/components/template-brand-menu';
import { filterTemplateCollection, type TemplateWithBrands } from '@shared/templateCollections';
import { Link } from 'wouter';
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { AdMediaDialog } from '@/components/ad-media-dialog';
import { withImpersonation } from "@/lib/impersonation";
import { useVideoEnabled, useVideoGateCopy } from '@/hooks/use-video-enabled';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Masonry from 'react-masonry-css';
import {
  Search,
  X,
  Download,
  Upload,
  Loader2,
  Trash2,
  Plus,
  FolderArchive,
  Folder,
  Filter,
  Layers,
  Palette,
  LayoutGrid,
  ArrowUpDown,
  Check,
  Copy,
  Eye,
  ChevronDown,
  Play,
  Heart,
  ExternalLink,
  Bookmark,
  Globe,
  Facebook,
  Instagram,
  Clock,
  Calendar,
  Image,
  Video,
  MonitorPlay,
  BookmarkPlus,
  TrendingUp,
  Music,
  Hash,
  MessageCircle,
  Share2,
  AlertTriangle,
  CheckSquare,
  Square,
  Volume2,
  VolumeX,
  Film,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useCanvasStore } from './canvas-store';
import { apiRequest, queryClient as sharedQueryClient } from '@/lib/queryClient';
import { isHoverSoundEnabled, setHoverSoundEnabled } from '@/lib/hover-sound';
import type { AdTemplate, OrganicPost } from '@shared/schema';
import { importDiscoveryImages } from './discovery-canvas-import';
import { CloneDestinationPicker } from './canvas-clone-picker';
import { runCloneForSource } from './canvas-clone-runner';
import { pickProjectAndPrepareVideoClone } from './video-clone-runner';
import { adStyleOf, AD_STYLE_OPTIONS } from './ad-style';
import type { CanvasImage } from './canvas-types';

// ─── Shared Types ────────────────────────────────────────────────────────────
type AdLibTab = 'my-ads' | 'competitors' | 'ad-templates' | 'find-ads' | 'organic-posts' | 'assets';

// ─── Ad Templates Types ───────────────────────────────────────────────────────
type TemplateActiveFilter = { category: string; value: string; label: string };
type SortOption = 'shuffled' | 'niche' | 'newest' | 'oldest' | 'name_asc' | 'name_desc';

const NICHE_OPTIONS = [
  'Ecommerce', 'Nutra', 'Skincare', 'Weightloss', 'Fashion', 'SaaS',
  'Food & Beverage', 'Fitness', 'Health', 'Beauty', 'Tech', 'Finance',
  'Education', 'Travel', 'Real Estate', 'Automotive', 'Entertainment',
  'Gaming', 'Pets', 'Home & Garden', 'Sports', 'Luxury', 'B2B', 'Agency',
  'Supplements', 'CBD', 'Dental', 'Hair Care', 'Anti-Aging', 'Wellness', 'UGC',
];
// Style filter = the accurate per-ad AD STYLE (parsed from the name), matching
// how the Discover sections now organize. Replaces the old loose styleTags list.
const STYLE_OPTIONS = AD_STYLE_OPTIONS;
const FORMAT_OPTIONS = ['Square', 'Portrait', 'Landscape', 'Story', 'Banner'];

// ─── Find Ads Types ────────────────────────────────────────────────────────────
interface SearchResult {
  ad_id: string;
  page_name: string;
  page_id: string;
  page_logo_url: string;
  ad_creative_body: string;
  ad_creative_link_title: string;
  ad_creative_link_description: string;
  ad_creative_link_url: string;
  cta_type: string;
  media_type: 'image' | 'video' | 'carousel';
  media_url: string;
  media_thumbnail_url: string;
  snapshot_url: string;
  platform: string;
  platforms: string[];
  status: string;
  ad_start_date: string;
  ad_end_date: string | null;
  ad_start_date_raw: string;
  ad_end_date_raw: string | null;
  languages: string[];
  search_country: string;
}
type FindAdsSortOption = 'newest' | 'oldest' | 'longest_running' | 'top_impressions';
type DatePreset = '7d' | '14d' | '30d' | '90d' | '365d' | 'all';

// ─── Organic Posts Types ───────────────────────────────────────────────────────
interface TikTokSearchPost {
  externalId: string;
  creatorName: string;
  creatorUsername: string;
  creatorAvatar: string;
  caption: string;
  videoUrl: string;
  thumbnailUrl: string;
  musicName: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  hashtags: string[];
  isSponsored: boolean;
  postDate: string;
}
type OrganicSortOption = 'views' | 'likes' | 'comments' | 'shares' | 'newest';
type OrganicFilterType = 'all' | 'organic' | 'sponsored';
type CategoryFilter = 'all' | 'health_beauty' | 'gadgets_ecom' | 'biz_opp';
const CATEGORY_LABELS: Record<CategoryFilter, string> = {
  all: 'All',
  health_beauty: 'Health & Beauty',
  gadgets_ecom: 'Gadgets / Ecom',
  biz_opp: 'Biz Opp',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatCount(num: number): string {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(num);
}

function calculateDaysRunning(startDate: string): number {
  try {
    const start = new Date(startDate);
    const now = new Date();
    return Math.max(1, Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
  } catch { return 0; }
}

function formatDaysAgo(startDate: string): string {
  const days = calculateDaysRunning(startDate);
  if (days <= 0) return '';
  if (days === 1) return '1D';
  return `${days}D`;
}

function isSnapshotUrl(url: string): boolean {
  return url.includes('facebook.com/ads/archive/render_ad');
}

function cleanTemplateText(text: string): string {
  if (!text) return '';
  return text.replace(/\{\{[^}]+\}\}/g, '').trim();
}

function truncateText(text: string, max: number): string {
  if (!text || text.length <= max) return text || '';
  return text.slice(0, max) + '...';
}

function getDateFromPreset(preset: DatePreset): Date | null {
  if (preset === 'all') return null;
  const days = { '7d': 7, '14d': 14, '30d': 30, '90d': 90, '365d': 365 }[preset];
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
}

function formatDateForApi(d: Date): string {
  return d.toISOString().split('T')[0];
}

function getPresetLabel(preset: DatePreset): string {
  const labels: Record<DatePreset, string> = {
    '7d': 'Last 7 days', '14d': 'Last 14 days', '30d': 'Last 30 days',
    '90d': 'Last 90 days', '365d': 'Last 365 days', 'all': 'All Time',
  };
  return labels[preset];
}

function extractVideoId(url: string): string | null {
  const match = url.match(/\/video\/(\d+)/);
  return match ? match[1] : null;
}

/** 8 → "0:08", 75 → "1:15" — duration badge on video template cards. */
function formatVideoDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ─── Shared Small Components ──────────────────────────────────────────────────
function ActionIcon({ children, title, onClick, danger }: { children: React.ReactNode; title: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        ...templateCardActionStyle,
        ...(danger ? { background: 'rgba(220,38,38,0.85)', color: '#fff' } : {}),
      }}
    >
      {children}
    </button>
  );
}

// ─── Hover-play video card (shared: Find Ads results + video templates) ──────
// Chrome blocks UNMUTED autoplay before any user gesture — hover is not a
// gesture. Hover always starts playback immediately; sound defaults ON
// (v5.17 — the clicks that navigated here satisfy Chrome's sticky-activation
// requirement in practice, and the NotAllowedError→muted fallback below
// covers the cold case) unless the user stored an explicit preference via
// the speaker toggle. Clicking the toggle IS a gesture, so unmute-on-click
// always works. The preference is global (module-level + localStorage) so
// one click flips every video card on both tabs.
// v5.17 — pref extracted to @/lib/hover-sound so the canvas VideoTile shares
// the same global speaker state (one click flips every hover video app-wide).

function HoverVideoCard({ videoUrl, thumbnailUrl, alt, durationBadge, testId, overlay, format }: {
  format?: string;
  videoUrl: string;
  thumbnailUrl?: string;
  alt?: string;
  /** Optional "0:08"-style badge on the idle poster (bottom-left). Video
      template cards do NOT pass it — their chip row already carries it. */
  durationBadge?: string;
  testId?: string;
  /** Rendered inside the hover root (shares its relative stacking context), so
      hovering it does NOT trip the root's mouseleave and kill playback. */
  overlay?: React.ReactNode;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  // Mirrors the module-level pref so the speaker icon re-renders; re-synced on
  // every hover in case another card's toggle flipped it since we last played.
  const [soundOn, setSoundOn] = useState(isHoverSoundEnabled());

  // Unmuted play() rejected (NotAllowedError — no qualifying gesture yet):
  // never leave a dead card. Retry muted and show the speaker muted.
  const playMutedFallback = (v: HTMLVideoElement) => {
    v.muted = true;
    setSoundOn(false);
    v.play().catch(() => {});
  };

  const handleEnter = () => {
    setIsHovering(true);
    setSoundOn(isHoverSoundEnabled());
    const v = videoRef.current;
    if (!v) return;
    v.muted = !isHoverSoundEnabled();
    v.play().catch(() => { if (!v.muted) playMutedFallback(v); });
  };

  const handleLeave = () => {
    setIsHovering(false);
    const v = videoRef.current;
    if (v) { v.pause(); v.currentTime = 0; }
  };

  const toggleSound = (e: React.MouseEvent) => {
    e.stopPropagation(); // cards have their own onClick (selection / detail)
    const next = !soundOn;
    setHoverSoundEnabled(next);
    setSoundOn(next);
    const v = videoRef.current;
    if (!v) return;
    v.muted = !next; // applies live — the click is the gesture, so unmute sticks
    if (next && v.paused && isHovering) v.play().catch(() => playMutedFallback(v));
  };

  return (
    <div data-testid={testId} style={{ position: 'relative' }} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      {/* The reserved poster slot remains mounted during hover and errors. */}
      <LibraryMedia src={thumbnailUrl || videoUrl} alt={alt || ''} format={format} unavailable="Video preview unavailable" />
      {!isHovering && (
        <>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Play style={{ width: 16, height: 16, color: 'white' }} fill="white" />
            </div>
          </div>
          {durationBadge && (
            <span style={{ position: 'absolute', bottom: 8, left: 8, padding: '2px 7px', borderRadius: 10, background: 'rgba(0,0,0,0.65)', color: '#DCE3ED', fontSize: 10, fontWeight: 600, fontVariantNumeric: 'tabular-nums', pointerEvents: 'none' }}>{durationBadge}</span>
          )}
        </>
      )}
      <video ref={videoRef} src={videoUrl} muted loop playsInline preload="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: isHovering ? 'block' : 'none' }} crossOrigin="anonymous" />
      {isHovering && (
        <button
          data-testid="hover-video-mute-toggle"
          aria-label={soundOn ? 'Mute video previews' : 'Unmute video previews'}
          data-sound={soundOn ? 'on' : 'off'}
          onClick={toggleSound}
          style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 3, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#FFFFFF' }}
        >
          {soundOn ? <Volume2 style={{ width: 14, height: 14 }} /> : <VolumeX style={{ width: 14, height: 14 }} />}
        </button>
      )}
      {overlay}
    </div>
  );
}

// ─── Ad Templates Components ──────────────────────────────────────────────────
/** A video template shows the analyzing overlay (and holds Clone) only while
 *  work is genuinely in flight: status null right after upload/save, or
 *  'analyzing' while it runs. 'deferred' (v6.94: over the save-time size cap)
 *  and 'failed' are CLONEABLE — prepare backfills the analysis inline. A null
 *  older than 10 minutes is an orphan (a deploy restart killed the background
 *  ingest before it stamped a status) — treat it as deferred instead of
 *  spinning forever; prepare backfills those too. Images are never gated. */
function isAwaitingAnalysis(t: { mediaType?: string | null; videoAnalysisStatus?: string | null; createdAt?: string | Date | null }): boolean {
  if (t.mediaType !== 'video') return false;
  if (t.videoAnalysisStatus === 'analyzing') return true;
  if (t.videoAnalysisStatus != null) return false;
  const ageMs = t.createdAt ? Date.now() - new Date(t.createdAt as string | Date).getTime() : Infinity;
  return ageMs < 10 * 60_000;
}

/** "..." that cycles, so a long analysis still looks alive. */
function AnimatedDots() {
  const [n, setN] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setN((v) => (v + 1) % 4), 400);
    return () => clearInterval(id);
  }, []);
  return <span style={{ display: 'inline-block', width: '1.4em', textAlign: 'left' }}>{'.'.repeat(n)}</span>;
}

const templateMasonryBreakpoints = { default: 3, 1200: 3, 900: 2, 600: 1 };

function TemplateCard({
  template,
  onClick,
  onDelete,
  isSelected,
  onToggleSelect,
  selectionDisabled,
}: {
  template: TemplateWithBrands;
  onClick: () => void;
  onDelete: () => void;
  isSelected: boolean;
  onToggleSelect: () => void;
  /** True when the 10-select cap is hit and this card isn't already selected. */
  selectionDisabled: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const activeBrandId = useCanvasStore(state => state.activeBrandId);
  const competitor = template as Partial<DiscoverCompetitor & DiscoverMyAd>;
  // v2.32 — one click on the card toggles selection (single or multi). The
  // "Select" mode toggle is gone; the dark center overlay + Clone/Download
  // hover buttons are gone. Top-right actions appear together on hover/focus.
  return (
    <div
      data-testid={`card-template-${template.id}`}
      className="discover-template-card"
      style={{
        marginBottom: 12, borderRadius: 10, overflow: 'hidden',
        // v4.72 — masonry columns are flex:1, so a sparse grid (e.g. "My
        // Templates" with one save) balloons its column to full width and
        // the card renders as a giant banner. Cap at normal column width.
        maxWidth: 560,
        cursor: selectionDisabled ? 'not-allowed' : 'pointer',
        position: 'relative', background: '#FFFFFF',
        border: isSelected ? '2px solid #1A1A1A' : '1px solid #DCE3ED',
        boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.10)' : '0 1px 4px rgba(0,0,0,0.05)',
        transition: 'box-shadow 150ms, border-color 150ms',
        opacity: selectionDisabled ? 0.5 : 1,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => { if (!selectionDisabled) onToggleSelect(); }}
    >
      <div style={{ position: 'relative' }}>
        {/* v4.69 — broken image → placeholder frame, NOT display:none. Hiding
            the img made the whole card invisible while still counted ("1
            Template" over an empty grid), with no way to see or delete it. */}
        {template.mediaType === 'video' && template.videoUrl ? (
          // Video templates: hover-play with sound (shared component). Poster
          // is the stored poster frame; chip/duration/checkbox/hover-action
          // overlays stay in THIS component (zIndex 2, top corners) — the
          // speaker button sits bottom-right so nothing fights.
          <HoverVideoCard testId={`hover-video-${template.id}`} videoUrl={template.videoUrl} thumbnailUrl={template.imageUrl} alt={template.name} format={template.format} />
        ) : (
          <LibraryMedia src={template.imageUrl} alt={template.name} format={template.format} />
        )}
        {/* Selection indicator (top-left). Shows on hover or when selected.
            Non-interactive — the whole card click toggles selection. */}
        {(hovered || isSelected) && (
          <div
            data-testid={`template-checkbox-${template.id}`}
            style={{
              position: 'absolute', top: 8, left: 8, zIndex: 2,
              width: 26, height: 26, borderRadius: 6,
              background: 'rgba(255,255,255,0.95)', border: '1px solid #DCE3ED',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {isSelected
              ? <CheckSquare style={{ width: 15, height: 15, color: '#1A1A1A' }} />
              : <Square style={{ width: 15, height: 15, color: '#5F6D82' }} />}
          </div>
        )}
        <div className="discover-template-actions" data-testid={`template-card-actions-${template.id}`} style={{ position: 'absolute', top: 8, right: 8, zIndex: 4, display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <ActionIcon title="View" onClick={onClick}><Eye style={{ width: 13, height: 13 }} /></ActionIcon>
          {!template.isGlobal && <ActionIcon title="Delete" danger onClick={onDelete}><Trash2 style={{ width: 13, height: 13 }} /></ActionIcon>}
          <SaveTemplateBrandMenu source={competitor.myAdId ? {myAdId:competitor.myAdId} : competitor.winningAdId ? {winningAdId:competitor.winningAdId} : { templateId: template.id, brandIds: template.brandIds }} preferredBrandId={competitor.myAdId || competitor.winningAdId ? activeBrandId : undefined} testId={`button-save-template-brand-${template.id}`} iconOnly style={templateCardActionStyle} />
        </div>
        {/* Top-left video chip + duration badge (drops below the selection
            checkbox whenever the checkbox is visible). Mirrors the Task 12
            chip on canvas-ad-templates-view.tsx — same testid convention. */}
        {isAwaitingAnalysis(template) && (
          <div
            data-testid={`template-analyzing-${template.id}`}
            style={{
              position: 'absolute', inset: 0, zIndex: 3,
              background: 'rgba(240,237,232,0.82)', backdropFilter: 'blur(1.5px)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
              pointerEvents: 'none',
            }}
          >
            <span style={{ fontSize: 12.5, fontWeight: 600, color: '#334155' }} className="animate-pulse">
              Analyzing<AnimatedDots />
            </span>
            <span style={{ fontSize: 11, color: '#5F6D82' }}>scene breakdown</span>
          </div>
        )}
        {template.mediaType === 'video' && (
          <div
            data-testid={`chip-video-template-${template.id}`}
            style={{ position: 'absolute', top: hovered || isSelected ? 40 : 8, left: 8, zIndex: 2, display: 'flex', gap: 4, pointerEvents: 'none' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 10, background: 'rgba(0,0,0,0.65)', color: '#FFFFFF', fontSize: 10, fontWeight: 600, backdropFilter: 'blur(4px)' }}>
              Video
            </span>
            {template.videoDurationSec != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: 10, background: 'rgba(0,0,0,0.65)', color: '#DCE3ED', fontSize: 10, fontWeight: 600, backdropFilter: 'blur(4px)', fontVariantNumeric: 'tabular-nums' }}>
                {formatVideoDuration(template.videoDurationSec)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateDetailModal({ template, onClose, onClone, onDelete, onUseAsReference }: {
  template: AdTemplate; onClose: () => void; onClone: () => void; onDelete: () => void; onUseAsReference: () => void;
}) {
  // v7.04 — video cloning is admin-only while the feature is being built.
  const videoEnabled = useVideoEnabled();
  const videoGateCopy = useVideoGateCopy();
  const videoLocked = template.mediaType === 'video' && !videoEnabled;
  return (
    <AdMediaDialog title={template.name} imageUrl={template.imageUrl} videoUrl={template.videoUrl || undefined} onClose={onClose} previewTestId={`img-template-preview-${template.id}`} footer={<>
          {!(template as Partial<DiscoverCompetitor>).winningAdId && !(template as Partial<DiscoverMyAd>).myAdId && <button onClick={onDelete} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #F5C6C6', background: 'transparent', color: '#DC2626', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FEF2F2')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}><Trash2 style={{ width: 13, height: 13 }} />Delete</button>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={onUseAsReference} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #DCE3ED', background: '#FFFFFF', color: '#334155', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FFFFFF')}><Eye style={{ width: 13, height: 13 }} />Use as reference</button>
            <button onClick={() => { if (!videoLocked) onClone(); }} disabled={videoLocked} title={videoLocked ? videoGateCopy.long : undefined} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #DCE3ED', background: videoLocked ? '#F1F5F9' : '#FFFFFF', color: videoLocked ? '#94A3B8' : '#334155', fontSize: 12.5, fontWeight: 500, cursor: videoLocked ? 'default' : 'pointer' }} onMouseEnter={(e) => { if (!videoLocked) (e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9'; }} onMouseLeave={(e) => { if (!videoLocked) (e.currentTarget as HTMLButtonElement).style.background = '#FFFFFF'; }}><Copy style={{ width: 13, height: 13 }} />{videoLocked ? videoGateCopy.short : template.mediaType === 'video' ? 'Clone this video ad' : 'Clone this ad'}</button>
            <a href={adSourceDownloadUrl(template)} aria-disabled={!adSourceDownloadUrl(template)} download={template.name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', background: '#1A1A1A', color: '#FFFFFF', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', textDecoration: 'none' }}><Download style={{ width: 13, height: 13 }} />Download</a>
          </div>
    </>}>
            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#5F6D82', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Format</span>
              <p style={{ fontSize: 13, color: '#334155', marginTop: 4, textTransform: 'capitalize' }}>{template.format || '—'}</p>
            </div>
            {template.niche && template.niche.length > 0 && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#5F6D82', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Niche</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                  {template.niche.map((n) => <span key={n} style={{ fontSize: 11, fontWeight: 500, padding: '3px 8px', borderRadius: 20, background: '#EAF0F7', color: '#526074', border: '1px solid #DCE3ED' }}>{n}</span>)}
                </div>
              </div>
            )}
            {adStyleOf(template) && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#5F6D82', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Style</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 8px', borderRadius: 20, background: '#EAF0F7', color: '#526074', border: '1px solid #DCE3ED' }}>{adStyleOf(template)}</span>
                </div>
              </div>
            )}
            {((template as Partial<DiscoverCompetitor>).winningAdId || (template as Partial<DiscoverMyAd>).myAdId) && <>
              <div><span>Advertiser</span><p>{(template as DiscoverCompetitor).advertiserName}</p></div>
              {(template as DiscoverCompetitor).adAngle && <div><span>Ad angle</span><p>{(template as DiscoverCompetitor).adAngle}</p></div>}
              {(template as DiscoverCompetitor).sourceUrl && <a href={(template as DiscoverCompetitor).sourceUrl!} target="_blank" rel="noopener noreferrer">View ad source</a>}
            </>}
            {template.description && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#5F6D82', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Description</span>
                <p style={{ fontSize: 12.5, color: '#526074', marginTop: 4, lineHeight: 1.5 }}>{template.description}</p>
              </div>
            )}

    </AdMediaDialog>
  );
}

/** First-frame capture for a local video file: poster preview + the frame
 *  blob sent to the categorizer (the vision endpoint is image-only). Seeks
 *  ~0.5s in — frame 0 of many video ads is black. */
function captureVideoFrame(file: File): Promise<{ dataUrl: string; blob: Blob; duration: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    const fail = (msg: string) => { URL.revokeObjectURL(url); reject(new Error(msg)); };
    video.onerror = () => fail('Could not read this video');
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(0.5, (video.duration || 1) / 2);
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return fail('Canvas unavailable');
        ctx.drawImage(video, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return fail('Frame capture failed');
          resolve({ dataUrl, blob, duration: video.duration || 0 });
        }, 'image/jpeg', 0.85);
      } catch {
        fail('Frame capture failed');
      }
    };
    video.src = url;
  });
}

/** Turn a failed upload Response into a readable message. The app sits behind
 *  Cloudflare + nginx, which answer with HTML — not JSON — for body-size (413)
 *  and 100s-timeout (524) failures, so res.json() here threw a parse error and
 *  masked the real cause. */
const CHUNK_BYTES = 8 * 1024 * 1024;
/** Threshold above which we switch to chunked upload — comfortably under the
 *  droplet nginx body cap (~50MB) so a single-shot POST can't 413. */
const CHUNK_ABOVE_BYTES = 20 * 1024 * 1024;

async function uploadVideoInChunks({
  file,
  fields,
  onProgress,
}: {
  file: File;
  fields: { name: string; description: string; niche: string[]; styleTags: string[] };
  onProgress?: (pct: number) => void;
}): Promise<void> {
  const begin = await fetch('/api/uploads/begin', { method: 'POST', credentials: 'include' });
  if (!begin.ok) throw new Error(await uploadErrorMessage(begin));
  const { uploadId } = await begin.json();

  const total = Math.ceil(file.size / CHUNK_BYTES);
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * CHUNK_BYTES, Math.min((i + 1) * CHUNK_BYTES, file.size));
    const fd = new FormData();
    fd.append('chunk', blob, 'part');
    fd.append('uploadId', uploadId);
    fd.append('index', String(i));
    const res = await fetch('/api/uploads/chunk', { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) throw new Error(await uploadErrorMessage(res));
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }

  const done = await fetch('/api/uploads/finish-template', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uploadId, mimetype: file.type || 'video/mp4', ...fields }),
  });
  if (!done.ok) throw new Error(await uploadErrorMessage(done));
}

async function uploadErrorMessage(res: Response): Promise<string> {
  const body = await res.text().catch(() => '');
  try {
    const parsed = JSON.parse(body);
    if (parsed?.message) return parsed.message;
    if (parsed?.error) return parsed.error;
  } catch {
    /* HTML or empty — fall through to the status-based messages below */
  }
  if (res.status === 413) return 'File too large for the server to accept. Videos must be under 70MB.';
  if (res.status === 504 || res.status === 524) {
    return 'The upload took too long and timed out (limit is ~100s). Try a shorter or smaller video, or a faster connection.';
  }
  if (res.status === 502 || res.status === 503) return 'The server was unavailable during the upload. Please try again.';
  if (res.status === 401) return 'Your session expired — reload the page and sign in again.';
  return `Upload failed (${res.status}). ${body.slice(0, 120)}`.trim();
}

function UploadModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadData, setUploadData] = useState({ name: '', description: '', niche: [] as string[], format: 'square', styleTags: [] as string[] });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isVideo = !!file && file.type.startsWith('video/');

  const handleFile = useCallback(async (f: File) => {
    const isVid = f.type.startsWith('video/');
    if (isVid && f.size > 300 * 1024 * 1024) {
      toast({ title: 'Video too large', description: 'Video ads are capped at 300MB.', variant: 'destructive' });
      return;
    }
    setFile(f);
    setVideoDuration(null);
    setAnalyzing(true);
    try {
      // Videos: categorize from a captured frame (the vision endpoint is
      // image-only); the frame also becomes the preview.
      let analyzeBlob: Blob = f;
      let analyzeName = f.name;
      if (isVid) {
        try {
          const frame = await captureVideoFrame(f);
          setPreview(frame.dataUrl);
          setVideoDuration(frame.duration);
          analyzeBlob = frame.blob;
          analyzeName = 'frame.jpg';
        } catch {
          setPreview(null);
          setAnalyzing(false);
          return; // no frame → no auto-categorize; fields stay manual
        }
      } else {
        setPreview(URL.createObjectURL(f));
      }
      const formData = new FormData();
      formData.append('file', analyzeBlob, analyzeName);
      const res = await fetch('/api/ad-templates/analyze', { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Analysis failed');
      const result = await res.json();
      setUploadData({ name: result.name || '', description: result.description || '', niche: result.niche || [], format: result.format || 'square', styleTags: result.styleTags || [] });
    } catch {
      toast({ title: 'Auto-analysis failed', description: 'You can fill in the details manually.', variant: 'destructive' });
    } finally { setAnalyzing(false); }
  }, [toast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && (f.type.startsWith('image/') || f.type.startsWith('video/'))) handleFile(f);
  }, [handleFile]);

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', uploadData.name || 'Untitled Template');
      formData.append('description', uploadData.description || '');
      formData.append('niche', JSON.stringify(uploadData.niche));
      formData.append('format', uploadData.format);
      formData.append('styleTags', JSON.stringify(uploadData.styleTags));
      formData.append('uploadedBy', 'user');
      if (file.type.startsWith('video/') && file.size > CHUNK_ABOVE_BYTES) {
        await uploadVideoInChunks({
          file,
          fields: {
            name: uploadData.name || 'Untitled Template',
            description: uploadData.description || '',
            niche: uploadData.niche,
            styleTags: uploadData.styleTags,
          },
          onProgress: setUploadPct,
        });
      } else {
        const res = await fetch('/api/ad-templates', { method: 'POST', body: formData });
        if (!res.ok) throw new Error(await uploadErrorMessage(res));
      }
      queryClient.invalidateQueries({ queryKey: ['/api/ad-templates'] });
      toast({ title: 'Template uploaded successfully' });
      onSuccess(); onClose();
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploading(false); setUploadPct(0); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#F7F9FC', border: '1px solid #DCE3ED', borderRadius: 16, width: '90%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #DCE3ED' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>Upload Template</span>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: 'transparent', color: '#5F6D82', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}><X style={{ width: 15, height: 15 }} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,video/mp4,video/quicktime,video/webm" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          {!file ? (
            <div data-testid="dropzone-upload-template" style={{ border: `2px dashed ${isDragOver ? '#5F6D82' : '#C8D3E2'}`, borderRadius: 12, padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: isDragOver ? '#F1F5F9' : '#F7F9FC', cursor: 'pointer', transition: 'all 150ms' }} onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }} onDragLeave={() => setIsDragOver(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}>
              <Upload style={{ width: 28, height: 28, color: '#5F6D82' }} />
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 14, fontWeight: 500, color: '#334155' }}>Drop an image or video here, or click to browse</p>
                <p style={{ fontSize: 12, color: '#5F6D82', marginTop: 4 }}>PNG, JPG, WebP, MP4, MOV — AI will auto-categorize. Video ads become cloneable.</p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                {preview && (
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <img src={preview} alt="Preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #DCE3ED', display: 'block' }} />
                    {isVideo && (
                      <span style={{ position: 'absolute', bottom: 4, left: 4, padding: '1px 6px', borderRadius: 8, background: 'rgba(0,0,0,0.65)', color: '#DCE3ED', fontSize: 9.5, fontWeight: 600 }}>
                        {videoDuration != null && videoDuration > 0 ? formatVideoDuration(videoDuration) : 'Video'}
                      </span>
                    )}
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  {analyzing && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#EAF0F7', borderRadius: 8, marginBottom: 10 }}>
                      <Loader2 style={{ width: 14, height: 14, color: '#5F6D82' }} className="animate-spin" />
                      <span style={{ fontSize: 12.5, color: '#526074' }}>AI is categorizing your template…</span>
                    </div>
                  )}
                  <label style={{ fontSize: 12, fontWeight: 500, color: '#526074', display: 'block', marginBottom: 4 }}>Name</label>
                  <input data-testid="input-canvas-template-name" value={uploadData.name} onChange={(e) => setUploadData((d) => ({ ...d, name: e.target.value }))} placeholder="Template name" style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 13, color: '#1A1A1A', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#526074', display: 'block', marginBottom: 4 }}>Format</label>
                <select value={uploadData.format} onChange={(e) => setUploadData((d) => ({ ...d, format: e.target.value }))} style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 13, color: '#1A1A1A', outline: 'none' }}>
                  {FORMAT_OPTIONS.map((f) => <option key={f} value={f.toLowerCase()}>{f}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#526074', display: 'block', marginBottom: 4 }}>Description</label>
                <textarea value={uploadData.description} onChange={(e) => setUploadData((d) => ({ ...d, description: e.target.value }))} placeholder="Optional description" rows={2} style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 13, color: '#1A1A1A', outline: 'none', resize: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
          )}
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid #DCE3ED', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #DCE3ED', background: 'transparent', color: '#526074', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
          <button data-testid="button-canvas-upload-submit" onClick={handleSubmit} disabled={!file || uploading || analyzing} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: !file || uploading || analyzing ? '#C8D3E2' : '#1A1A1A', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: !file || uploading || analyzing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            {uploading && <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" />}
            {uploading ? (uploadPct > 0 && uploadPct < 100 ? `Uploading… ${uploadPct}%` : 'Uploading…') : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

type ThumbStatus = 'pending' | 'extracting' | 'categorizing' | 'done' | 'failed';
interface ThumbItem { name: string; status: ThumbStatus; }

function ZipUploadModal({ onClose }: { onClose: () => void }) {
  const [uploading, setUploading] = useState(false);
  const [thumbs, setThumbs] = useState<ThumbItem[]>([]);
  const [result, setResult] = useState<{ success: number; failed: number } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const simulateThumbs = useCallback((count: number, successCount: number) => {
    const items: ThumbItem[] = Array.from({ length: count }, (_, i) => ({ name: `image_${i + 1}.jpg`, status: 'pending' as ThumbStatus }));
    setThumbs(items);
    let idx = 0;
    const interval = setInterval(() => {
      if (idx >= count) { clearInterval(interval); return; }
      const i = idx; idx++;
      setThumbs((prev) => prev.map((t, j) => j === i ? { ...t, status: 'extracting' } : t));
      setTimeout(() => {
        setThumbs((prev) => prev.map((t, j) => j === i ? { ...t, status: 'categorizing' } : t));
        setTimeout(() => {
          setThumbs((prev) => prev.map((t, j) => j === i ? { ...t, status: i >= successCount ? 'failed' : 'done' } : t));
        }, 600);
      }, 400);
    }, 150);
  }, []);

  const handleFile = async (file: File) => {
    setUploading(true); setResult(null);
    setThumbs([{ name: 'Uploading ZIP…', status: 'extracting' }]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/ad-templates/batch', { method: 'POST', body: formData });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Batch upload failed'); }
      const r = await res.json();
      const total = (r.success || 0) + (r.failed || 0);
      simulateThumbs(Math.max(total, 1), r.success || 0);
      setTimeout(() => {
        setResult({ success: r.success, failed: r.failed });
        queryClient.invalidateQueries({ queryKey: ['/api/ad-templates'] });
        toast({ title: 'Batch upload complete', description: `${r.success} template${r.success !== 1 ? 's' : ''} added${r.failed > 0 ? `, ${r.failed} failed` : ''}` });
      }, total * 150 + 1200);
    } catch (err: any) {
      setThumbs([]);
      toast({ title: 'Batch upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const phase = thumbs.length > 0 && !result;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#F7F9FC', border: '1px solid #DCE3ED', borderRadius: 16, width: '90%', maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #DCE3ED', flexShrink: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>Upload ZIP</span>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: 'transparent', color: '#5F6D82', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}><X style={{ width: 15, height: 15 }} /></button>
        </div>
        <div style={{ padding: '24px 20px', flex: 1, overflowY: 'auto' }}>
          <input ref={fileInputRef} type="file" accept=".zip" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          {!uploading && !phase && !result && (
            <div data-testid="dropzone-upload-zip" style={{ border: `2px dashed ${isDragOver ? '#5F6D82' : '#C8D3E2'}`, borderRadius: 12, padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: isDragOver ? '#F1F5F9' : '#F7F9FC', cursor: 'pointer', transition: 'all 150ms' }} onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }} onDragLeave={() => setIsDragOver(false)} onDrop={(e) => { e.preventDefault(); setIsDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }} onClick={() => fileInputRef.current?.click()}>
              <FolderArchive style={{ width: 28, height: 28, color: '#5F6D82' }} />
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 14, fontWeight: 500, color: '#334155' }}>Drop a ZIP file here or click to browse</p>
                <p style={{ fontSize: 12, color: '#5F6D82', marginTop: 4 }}>All images will be extracted and AI-categorized</p>
              </div>
            </div>
          )}
          {phase && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Loader2 style={{ width: 16, height: 16, color: '#5F6D82' }} className="animate-spin" />
                <span style={{ fontSize: 13.5, color: '#334155', fontWeight: 500 }}>Extracting and categorizing…</span>
              </div>
              <div style={{ width: '100%', height: 4, background: '#DCE3ED', borderRadius: 2, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ height: '100%', background: '#1A1A1A', borderRadius: 2, animation: 'progress-indeterminate 1.5s ease-in-out infinite', width: '40%' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                {thumbs.map((t, i) => (
                  <div key={i} data-testid={`zip-thumb-item-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 8, background: '#F7F9FC', border: '1px solid #DCE3ED' }}>
                    {t.status === 'done' ? <Check style={{ width: 11, height: 11, color: '#16a34a' }} /> : t.status === 'failed' ? <X style={{ width: 11, height: 11, color: '#DC2626' }} /> : <Loader2 style={{ width: 11, height: 11, color: '#5F6D82' }} className="animate-spin" />}
                    <span style={{ flex: 1, fontSize: 12.5, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                    <span style={{ fontSize: 11, color: '#5F6D82', flexShrink: 0, textTransform: 'capitalize' }}>{t.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ fontSize: 20, fontWeight: 700, color: '#1A1A1A' }}>{result.success} template{result.success !== 1 ? 's' : ''} added</p>
              {result.failed > 0 && <p style={{ fontSize: 13, color: '#5F6D82', marginTop: 4 }}>{result.failed} failed to process</p>}
              <button onClick={onClose} style={{ marginTop: 20, padding: '9px 20px', borderRadius: 10, border: 'none', background: '#1A1A1A', color: '#FFFFFF', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateFilterDropdown({ activeFilters, onAdd, onClose, available }: { available?: Record<string,string[]>; activeFilters: TemplateActiveFilter[]; onAdd: (category: string, value: string, label: string) => void; onClose: () => void; }) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const categories = [
    { key: 'Niche', icon: <Layers style={{ width: 12, height: 12 }} />, options: NICHE_OPTIONS },
    { key: 'Format', icon: <LayoutGrid style={{ width: 12, height: 12 }} />, options: FORMAT_OPTIONS },
    { key: 'Style', icon: <Palette style={{ width: 12, height: 12 }} />, options: STYLE_OPTIONS },
  ];
  if (available) { categories.forEach(category => { category.options = category.key === 'Format' ? FORMAT_OPTIONS : available[category.key] ?? []; }); categories.push({key:'Ad angles',icon:<Layers size={12}/>,options:available['Ad angles'] ?? []}); }
  const activeCat = categories.find((c) => c.key === activeCategory);
  const filteredOptions = activeCat ? activeCat.options.filter((o) => o.toLowerCase().includes(search.toLowerCase())) : [];
  return (
    <div data-testid="template-filter-menu" style={{ ...templateFilterPanelStyle, position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200, display: 'flex', minWidth: 320 }}>
      <div style={{ width: 130, borderRight: '1px solid #EAF0F7', padding: '6px 0' }}>
        {categories.map((cat) => (
          <button key={cat.key} onClick={() => { setActiveCategory(cat.key); setSearch(''); }} style={{ ...templateFilterItemStyle, color: activeCategory === cat.key ? '#1A1A1A' : '#334155', background: activeCategory === cat.key ? '#F1F5F9' : 'transparent', fontWeight: activeCategory === cat.key ? 500 : 400 }}>
            <span style={{ color: '#5F6D82' }}>{cat.icon}</span>{cat.key}
          </button>
        ))}
      </div>
      {activeCategory && activeCat && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', maxHeight: 280 }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #EAF0F7' }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${activeCategory.toLowerCase()}…`} style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 12, color: '#1A1A1A', outline: 'none', boxSizing: 'border-box' }} autoFocus />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
            {filteredOptions.map((opt) => {
              const isActive = activeFilters.some((f) => f.category === activeCategory && f.value === opt);
              return (
                <button key={opt} onClick={() => { onAdd(activeCategory, opt, opt); }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', fontSize: 12.5, color: '#334155', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}>
                  <span>{opt}</span>
                  {isActive && <Check style={{ width: 12, height: 12, color: '#1A1A1A', flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ad Templates Tab ─────────────────────────────────────────────────────────
function AdTemplatesTab({ onShowUpload, onShowZip, initialStyleFilters, initialFocusId, competitors = false, myAds = false }: { competitors?: boolean; myAds?: boolean; onShowUpload: () => void; onShowZip: () => void; initialStyleFilters?: string[]; initialFocusId?: number }) {
  const scannedAds = competitors || myAds;
  const galleryEndpoint = myAds ? '/api/discover/my-ads' : '/api/discover/competitors';
  const { sendMessage } = useCanvasStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // v7.04 — video cloning is admin-only while the feature is being built.
  const videoEnabled = useVideoEnabled();
  const videoGateCopy = useVideoGateCopy();
  const [searchQuery, setSearchQuery] = useState('');
  const [mediaFilter, setMediaFilter] = useState('all');
  const [activeFilters, setActiveFilters] = useState<TemplateActiveFilter[]>(
    () => (initialStyleFilters ?? []).map((v) => ({ category: 'Style', value: v, label: v })),
  );
  // When opened from a Discover style section (initialStyleFilters present),
  // present the category in a stable, organized order instead of the random
  // "Mixed" shuffle — otherwise the filtered category reads as a random pile.
  // 'niche' groups by product niche and doesn't cluster by brand.
  // v6.47 — the DEFAULT library view stays 'shuffled' (Mixed): it's the
  // winning-ads library and sorting it newest-first buried every global
  // behind the user's own recent uploads. Personal and brand collections use 'newest'
  // instead (see the brand filter) — that's the view where a fresh upload matters.
  const [sortBy, setSortBy] = useState<SortOption>(
    myAds ? 'name_asc' : (initialStyleFilters?.length ?? 0) > 0 ? 'niche' : 'shuffled',
  );
  const [templateCollection, setTemplateCollection] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AdTemplate | null>(null);
  const [importSources, setImportSources] = useState<AdTemplate[] | null>(null);
  const [clonePickerTemplate, setClonePickerTemplate] = useState<AdTemplate | null>(null);
  // Multi-select for "Clone N Ads" batch. Cap at 10 — 10 sources × 2 clones
  // each is already 20 parallel WaveSpeed calls.
  const SELECT_CAP = 10;
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBatchCloning, setIsBatchCloning] = useState(false);
  const [showBatchPicker, setShowBatchPicker] = useState(false);
  // v2.29 — incremental render. The global library is 855 templates; mounting
  // all of them (each fetching its Bunny image) on open was the slow path, and
  // native loading="lazy" is unreliable inside a scroll container. Render only
  // the first page and grow as the user scrolls a sentinel into view.
  const TEMPLATE_PAGE_SIZE = 48;
  const [visibleCount, setVisibleCount] = useState(TEMPLATE_PAGE_SIZE);
  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const toggleSelectId = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < SELECT_CAP) {
        next.add(id);
      }
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // v6.51 — poll while anything is still being analyzed so the "Analyzing…"
  // overlay clears itself the moment the scene breakdown lands. Idle libraries
  // don't poll (the queryClient default is staleTime: Infinity).
  const { data: templates = [], isLoading, isError, refetch } = useQuery<TemplateWithBrands[]>({
    queryKey: [scannedAds ? galleryEndpoint : '/api/ad-templates'],
    staleTime: myAds ? 0 : undefined,
    refetchOnMount: myAds ? 'always' : true,
    refetchInterval: (q) =>
      (q.state.data as AdTemplate[] | undefined)?.some(isAwaitingAnalysis) ? 6000 : false,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      if (scannedAds) throw new Error('Scanned ads are managed through their brand scan.');
      const res = await fetch(`/api/ad-templates/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['/api/ad-templates'] }); toast({ title: 'Template deleted' }); setSelectedTemplate(null); },
    onError: () => toast({ title: 'Delete failed', variant: 'destructive' }),
  });

  const addFilter = useCallback((category: string, value: string, label: string) => {
    setActiveFilters((prev) => { if (prev.some((f) => f.category === category && f.value === value)) return prev; return [...prev, { category, value, label }]; });
    setShowFilterDropdown(false);
  }, []);

  const removeFilter = useCallback((category: string, value: string) => {
    setActiveFilters((prev) => prev.filter((f) => !(f.category === category && f.value === value)));
  }, []);

  // v2.30 — stable per-load shuffle. The default "Mixed" view interleaves
  // brands/niches instead of showing every clone in insertion order (all Nello
  // beside each other, etc.). Recomputes only when the template set changes
  // (a fresh fetch), so order stays put while you scroll/filter but re-mixes
  // on reload.
  const templateIdsKey = templates.map(template => template.id).sort((a,b)=>a-b).join(',');
  const shuffleRank = useMemo(() => {
    const ids = templates.map((t) => t.id);
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    const m = new Map<number, number>();
    ids.forEach((id, idx) => m.set(id, idx));
    return m;
  }, [templateIdsKey]);

  const filteredTemplates = useMemo(() => {
    let results = scannedAds ? templates.filter(t => templateCollection === 'all' || (myAds ? String((t as DiscoverMyAd).sourceBrandId) : (t as DiscoverCompetitor).advertiserName) === templateCollection) : filterTemplateCollection(templates, templateCollection);
    if (mediaFilter !== 'all') results = results.filter(t => (t.mediaType || 'image') === mediaFilter);
    const angleFilters = activeFilters.filter(f=>f.category==='Ad angles').map(f=>f.value);
    if(angleFilters.length) results=results.filter(t=>angleFilters.includes((t as DiscoverCompetitor).adAngle || ''));
    const nicheFilters = activeFilters.filter((f) => f.category === 'Niche').map((f) => f.value);
    const formatFilters = activeFilters.filter((f) => f.category === 'Format').map((f) => f.value.toLowerCase());
    const styleFilters = activeFilters.filter((f) => f.category === 'Style').map((f) => f.value);
    if (nicheFilters.length > 0) results = results.filter((t) => (t.niche || []).some((n) => nicheFilters.includes(n)));
    if (formatFilters.length > 0) results = results.filter((t) => formatFilters.includes((t.format || '').toLowerCase()));
    if (styleFilters.length > 0) results = results.filter((t) => styleFilters.includes(adStyleOf(t)));
    if (searchQuery.trim()) {
      // v2.81 — tokenized search over a RICH haystack. Every word must appear
      // somewhere in: name, description, niche, ad style, AND the visual recipe/
      // brand (searchText from the source winning ad). AND-across-words means
      // multi-word queries ("blue minimalist sunscreen", "woman holding marble")
      // work, and the recipe makes ads searchable by what they look like.
      const words = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
      results = results.filter((t) => {
        const hay = [
          t.name,
          t.description,
          (t.niche || []).join(' '),
          adStyleOf(t),
          (t as unknown as { searchText?: string }).searchText || '',
        ].join(' ').toLowerCase();
        return words.every((w) => hay.includes(w));
      });
    }
    return [...results].sort((a, b) => {
      // Pin the ad clicked in a Discover section to the very top of its category.
      if (initialFocusId != null) {
        if (a.id === initialFocusId) return -1;
        if (b.id === initialFocusId) return 1;
      }
      switch (sortBy) {
        case 'newest': return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        case 'oldest': return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        case 'name_asc': return a.name.localeCompare(b.name);
        case 'name_desc': return b.name.localeCompare(a.name);
        case 'niche': {
          const an = (a.niche?.[0] || '~').toLowerCase(), bn = (b.niche?.[0] || '~').toLowerCase();
          return an === bn ? a.name.localeCompare(b.name) : an.localeCompare(bn);
        }
        default: return (shuffleRank.get(a.id) ?? 0) - (shuffleRank.get(b.id) ?? 0); // 'shuffled'
      }
    });
  }, [templates, searchQuery, activeFilters, sortBy, shuffleRank, initialFocusId, templateCollection, scannedAds, myAds, mediaFilter]);

  useEffect(() => {
    if (!scannedAds) return;
    const visibleIds = new Set(filteredTemplates.map(t => t.id));
    setSelectedIds(previous => {
      const next = new Set(Array.from(previous).filter(id => visibleIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [scannedAds, filteredTemplates]);

  // v2.29 — reset the visible window whenever the filtered set changes, so a
  // new search/filter/sort starts from the top instead of keeping a huge window.
  useEffect(() => { setVisibleCount(TEMPLATE_PAGE_SIZE); }, [searchQuery, activeFilters, sortBy, templateCollection, mediaFilter]);

  // v2.29 — grow the window when the bottom sentinel scrolls near the viewport.
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    const root = gridScrollRef.current;
    if (!sentinel || !root || visibleCount >= filteredTemplates.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => Math.min(c + TEMPLATE_PAGE_SIZE, filteredTemplates.length));
        }
      },
      { root, rootMargin: '800px' },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [filteredTemplates.length, visibleCount]);

  const handleUseAsReference = (template: AdTemplate) => {
    if (isVideoAdSource(template)) {
      toast({title:'Video ad',description:'Use the video clone flow. A video poster cannot be used as an image ad reference.'});
      return;
    }
    // v6.62 — with no open task the message goes to a null transcript and
    // silently vanishes (while still billing the chat turn). Block instead.
    if (useCanvasStore.getState().activeProjectId == null) {
      toast({ title: 'Open a task first', description: 'References are sent to a task\'s chat — open the task (under its brand) you want to use it in.', variant: 'destructive' });
      return;
    }
    void useCanvasStore.getState().sendAgentMessage(
      myAds ? `Use this ad from my brand as reference: ${template.name}` : competitors ? `Use this competitor ad as reference: ${template.name}` : 'Use this template as reference',
      undefined,
      {composerReferences:{selectedImageIds:[],chatAttachments:[{url:template.imageUrl,name:template.name}],attachedFile:null}},
    );
    toast({ title: 'Sent to canvas chat', description: `"${template.name}" added as reference.` });
    setSelectedTemplate(null);
  };

  /**
   * Clone a template: open the destination picker so the user can pick a
   * brand / task. The picker handles the actual canvas drop + auto-firing
   * 2 variations after a destination is chosen (see CloneDestinationPicker's
   * onPicked handlers).
   */
  const handleClone = (template: AdTemplate) => {
    // v7.04 — video cloning is admin-only while the feature is being built.
    if (isVideoAdSource(template) && !videoEnabled) {
      toast({ title: videoGateCopy.long, description: 'Image ads are ready to go — clone any image ad instead.' });
      return;
    }
    setClonePickerTemplate(isVideoAdSource(template) ? {...template,mediaType:'video'} : template);
    setSelectedTemplate(null);
  };

  /**
   * Step 1 of the batch-clone flow: open the destination picker so the user
   * can pick which brand task receives the clones. After they pick, the
   * picker's onPicked callback fires `runBatchCloneInto(projectId)` (see
   * below) which executes the actual canvas drops + parallel runners.
   */
  const handleBatchClone = () => {
    if (isBatchCloning || selectedIds.size === 0) return;
    const selected = filteredTemplates.filter((t) => selectedIds.has(t.id));
    if (selected.length === 0) return;
    const videos = selected.filter(isVideoAdSource);
    const images = selected.filter((t) => !isVideoAdSource(t));

    // Exactly ONE video selected, alone → the video-clone prepare flow. Route
    // through the single-template picker (its render site branches on
    // mediaType into 'video-clone' mode). One storyboard per source — the
    // ×2 clone count never applies.
    if (videos.length === 1 && images.length === 0) {
      setSelectedIds(new Set());
      handleClone(videos[0]);
      return;
    }

    // Video templates mixed into a batch (or several videos): they must NOT
    // go through the image pipeline — it would clone the poster frame, not
    // the video. Exclude them; the image members batch as usual.
    if (scannedAds && videos.length > 0) { toast({title:'Video ads clone one at a time',description:'Select one video, or select only image ads for a batch.'}); return; }
    if (videos.length > 0) {
      toast({
        title: 'Video ads clone one at a time',
        description: 'Select just the video ad and hit Clone.',
      });
      if (images.length === 0) return; // only videos selected — nothing to batch
      setSelectedIds(new Set(images.map((t) => t.id)));
    }
    setShowBatchPicker(true);
  };

  /**
   * Step 2 of the batch-clone flow: drop every selected template onto the
   * chosen project's canvas (sequentially, each on its own pre-computed
   * row), then fire one independent clone runner per source in parallel.
   * Each runner produces 2 clones via the shared canvas-clone-runner, which
   * uses the same `mode: 'clone'` server pipeline as the single-source flow.
   */
  const runBatchCloneInto = async (targetProjectId: number, productId?: number, productName?: string) => {
    console.log('[BatchClone] runBatchCloneInto called', { targetProjectId, selectedIdsSize: selectedIds.size, isBatchCloning });
    if (isBatchCloning) {
      console.warn('[BatchClone] early-return: isBatchCloning already true');
      return;
    }
    if (selectedIds.size === 0) {
      console.warn('[BatchClone] early-return: no selected ids');
      return;
    }
    // Videos never image-batch (the pipeline would clone the poster frame,
    // not the video) — handleBatchClone already stripped them from the
    // selection, this filter is the safety net if state drifted.
    const selectedTemplates = filteredTemplates.filter((t) => selectedIds.has(t.id) && !isVideoAdSource(t));
    console.log('[BatchClone] resolved templates:', selectedTemplates.length, 'of', selectedIds.size, 'selected');
    if (selectedTemplates.length === 0) {
      console.warn('[BatchClone] early-return: no templates matched selected ids — filteredTemplates may have changed');
      return;
    }

    setIsBatchCloning(true);
    try {
      const destinationStore = useCanvasStore.getState();
      if (destinationStore.projects[targetProjectId]?.loadedAt == null) await destinationStore.switchProject(targetProjectId);
      const destination = useCanvasStore.getState().projects[targetProjectId];
      if (!destination || destination.loadedAt == null || destination.loading) throw new Error('The clone destination could not be loaded. Reopen the task and try again.');
      const cloneSelection = cloneBatchSelection(useCanvasStore.getState(), targetProjectId, 'source');
      const sourceOutputSizes = (width: number, height: number) => cloneOutputSizes(cloneSelection.aspectRatios, cloneSelection.count, width, height, cloneSelection.imageModel);
      const {
        addImageToCanvas,
      } = useCanvasStore.getState();

      // Flip into the chat+canvas split view IMMEDIATELY — before any
      // async work. Otherwise the user briefly sees the empty "Ask
      // anything or try a suggestion" default screen during the
      // brand-resolve fetch, which makes it look like the clone request
      // didn't take.
      useCanvasStore.getState()._writeProjectView(targetProjectId, { activeView: 'chat-canvas', isCanvasOpen: true });

      // Resolve the brand attached to the destination project — switchProject
      // doesn't reload brandId on subsequent switches, and the runner relies
      // on it to look up the brand product photo + packaging description.
      let brandIdForBatch: number | null = destination.brandId ?? null;
      try {
        const projResp = await fetch(`/api/canvas/projects/${targetProjectId}`);
        if (projResp.ok) {
          const proj = await projResp.json();
          brandIdForBatch = proj.brandId ?? proj.brand_id ?? brandIdForBatch;
          if (brandIdForBatch) {
            useCanvasStore.getState()._writeProjectMetadata(targetProjectId, { brandId: brandIdForBatch });
            if (useCanvasStore.getState().activeProjectId === targetProjectId) useCanvasStore.setState({ activeBrandId: brandIdForBatch });
          }
        }
      } catch (err) {
        console.warn('[BatchClone] brand resolve failed:', err);
      }

      toast({
        title: `Cloning ${selectedTemplates.length} ad${selectedTemplates.length === 1 ? '' : 's'}`,
        description: brandIdForBatch
          ? `Generating ${cloneSelection.count} ${cloneSelection.aspectRatios && cloneSelection.aspectRatios.length > 1 ? "formats" : "clones"} of each — queued together.`
          : `No brand attached — packaging won't be enforced.`,
      });

      // Step 1: drop each source on canvas at an explicit (x, y) position
      // so the layout is deterministic and predictable:
      //
      //   [Source 1]
      //   [Clone 1a] [Clone 1b]
      //   [Source 2]
      //   [Clone 2a] [Clone 2b]
      //   ...
      //
      // Each source sits in its own row at x=START_X. The runner places that
      // source's 2 clones below at source.y + sourceH + SOURCE_TO_CLONES_GAP.
      // We pre-compute every source's Y so we can drop them all in PARALLEL
      // (no position-race since each call passes its own fixedX/fixedY).
      const existingLayout = useCanvasStore.getState().projects[targetProjectId];
      const START_X = canvasRunLeft([...(existingLayout?.images ?? []), ...Object.values(existingLayout?.canvasPlaceholders ?? {})]);
      // Horizontal gap between the clones in a row (matches GEN_GAP in the
      // runner).
      const CLONE_GAP = 24;
      // v6.74 — VERTICAL gap source → clone row (matches SOURCE_TO_CLONES_GAP
      // in the runner). The clone row's labeled frame extends 52px above the
      // clones; 24px drew the frame + pill on top of the source ad.
      const SOURCE_TO_CLONES_GAP = 90;
      // v6.73 — clearance between RUNS (canvas content → first source, and one
      // source's clone row → the next source). Each source+clones cluster gets
      // its own labeled frame that extends FRAME_PAD_TOP (52px) ABOVE its top
      // tile plus a label pill, and 24px below its bottom tile — and a frame
      // ADOPTS any tile it touches, stretching over unrelated rows. The old
      // 24px gap put every new frame + pill ON TOP of the row above (Dan:
      // "its all overlaying things weird"). 110px keeps frames in clear air.
      const RUN_CLEARANCE = 110;
      // Pre-load all dims so we know the heights and can stack source rows
      // correctly even when sources are different aspect ratios.
      const sourceMeta: Array<{ template: typeof selectedTemplates[number]; w: number; h: number; displayW: number; displayH: number }> = [];
      await Promise.all(selectedTemplates.map(async (template) => {
        try {
          const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
            const img = new window.Image();
            img.onload = () => resolve({ w: img.naturalWidth || 1080, h: img.naturalHeight || 1080 });
            img.onerror = () => reject(new Error('Image load failed'));
            img.src = template.imageUrl;
          });
          // MUST match addImageToCanvas's internal DEFAULT_WIDTH/DEFAULT_HEIGHT
          // (currently 300 — see canvas-store.ts). If this is higher, we
          // reserve more vertical space per source-row than the tile
          // actually occupies, leaving a huge empty gap between sources.
          const DEFAULT_DIM = 300;
          const scale = Math.min(DEFAULT_DIM / dims.w, DEFAULT_DIM / dims.h, 1);
          sourceMeta.push({
            template,
            w: dims.w,
            h: dims.h,
            displayW: Math.round(dims.w * scale),
            displayH: Math.round(dims.h * scale),
          });
        } catch (err) {
          console.error(`[BatchClone] dim probe failed for template ${template.id}:`, err);
        }
      }));
      // Preserve the user's selection order — Promise.all populates in
      // resolution order, but for layout consistency we want source 1 / 2 / 3
      // to match the order the user picked the cards.
      sourceMeta.sort((a, b) =>
        selectedTemplates.findIndex((t) => t.id === a.template.id) -
        selectedTemplates.findIndex((t) => t.id === b.template.id),
      );

      // Start below whatever's currently on canvas so we don't overwrite
      // existing tiles — counting in-flight placeholders too, so back-to-back
      // batches don't stack on a row that's still generating.
      const projForBottom = useCanvasStore.getState().projects[targetProjectId];
      const existingBottom = [
        ...(projForBottom?.images ?? []).map((im) => im.y + im.displayHeight),
        ...Object.values(projForBottom?.canvasPlaceholders ?? {}).map((ph) => ph.y + ph.height),
      ].reduce((max, b) => Math.max(max, b), 0);
      const startY = existingBottom > 0 ? existingBottom + RUN_CLEARANCE : 60;

      // Pre-compute every source's (x, y) up front, then fire all
      // addImageToCanvas calls in PARALLEL — each call passes its own
      // fixedX/fixedY so they don't race on the auto-positioning logic.
      let cursorY = startY;
      const sourcePositions: Array<{ meta: typeof sourceMeta[number]; x: number; y: number }> = [];
      for (const meta of sourceMeta) {
        sourcePositions.push({ meta, x: START_X, y: cursorY });
        // Reserve one source-row + clone-row, then frame clearance before the
        // next template's cluster.
        const slots = cloneOutputSlots({x: START_X, y: cursorY, displayWidth: meta.displayW, displayHeight: meta.displayH}, sourceOutputSizes(meta.w, meta.h));
        cursorY = Math.max(...slots.map(slot => slot.y + slot.height)) + RUN_CLEARANCE;
      }

      const sources: CanvasImage[] = [];
      await Promise.all(sourcePositions.map(async ({ meta, x, y }) => {
        try {
          await importBatchCloneSource(targetProjectId, {
            url: meta.template.imageUrl, width: meta.w, height: meta.h,
            name: meta.template.name || `template-${meta.template.id}.jpg`, x, y,
          }, addImageToCanvas);
          // Identify the newly-added image by matching (src, x, y) — these
          // three together uniquely identify our drop. Using an id-diff
          // against a "before" snapshot is unsafe here because all source
          // drops happen in PARALLEL: every callback observes the same
          // empty pre-drop snapshot, then `find(...)` returns the same
          // (first) image to every caller, breaking the per-runner source
          // identification downstream (causes both runners to clone the
          // same source and chat messages to show identical thumbnails).
          const proj = useCanvasStore.getState().projects[targetProjectId];
          const newImage = proj?.images.find(
            (i) => i.src === meta.template.imageUrl && i.x === x && i.y === y,
          );
          if (newImage) {
            sources.push(newImage);
          } else {
            console.error(`[BatchClone] source not found after add for template ${meta.template.id} at (${x}, ${y})`);
          }
        } catch (err) {
          console.error(`[BatchClone] failed to drop source for template ${meta.template.id}:`, err);
        }
      }));
      // Sort recovered sources to match the visual order (top-to-bottom by y).
      sources.sort((a, b) => a.y - b.y);

      if (sources.length === 0) {
        toast({ title: 'Batch clone failed', description: 'No source images could be added to canvas.', variant: 'destructive' });
        return;
      }

      // Auto-fit the canvas viewport to the WHOLE batch area (all sources
      // + their reserved clone rows). Each addImageToCanvas call set its own
      // pendingFitToRow targeting just the last-added image's row, so by
      // default the viewport ends up zoomed in on one source — making the
      // others look like they "didn't appear". A single batch-wide fit at
      // the end puts everything on screen at once. The maxY accounts for
      // the clone row that's about to land beneath each source.
      {
        const previewSlots = sources.flatMap(source => cloneOutputSlots(source, sourceOutputSizes(source.originalWidth || 1080, source.originalHeight || 1080)));
        const minX = Math.min(...sources.map(source=>source.x));
        const minY = Math.min(...sources.map(source=>source.y));
        const maxX = Math.max(...sources.map(source=>source.x+source.displayWidth),...previewSlots.map(slot=>slot.x+slot.width));
        const maxY = Math.max(...previewSlots.map(slot=>slot.y+slot.height));
        useCanvasStore.getState()._writeProjectView(targetProjectId, {
          pendingFitToRow: { minX, minY, maxX, maxY },
          isCanvasOpen: true,
        });
      }

      // Persist every request immediately. The server queue owns the five-job cap,
      // so closing this browser cannot discard the remainder of a local pool.
      await Promise.all(sources.map(source => runCloneForSource({
        source, ...cloneSelection, queueOnly: true, projectId: targetProjectId, brandId: brandIdForBatch,
        productId, productName, suppressAutoFit: true,
      }).catch(err => console.error(`[BatchClone] runner failed for source ${source.id}:`, err))));

    } catch (err: any) {
      console.error('[BatchClone] fatal:', err);
      toast({ title: 'Batch clone failed', description: err.message, variant: 'destructive' });
    } finally {
      setIsBatchCloning(false);
      setSelectedIds(new Set());
    }
  };

  const sortLabels: Record<SortOption, string> = { shuffled: 'Mixed', niche: 'By Niche', newest: 'Newest', oldest: 'Oldest', name_asc: 'Name A-Z', name_desc: 'Name Z-A' };

  // Video/image split of the current selection, for the footer batch bar: a
  // lone video routes to the video-clone flow (one storyboard per source — no
  // "×2 clones" count), and mixed selections only image-batch the non-video
  // members, so the clone count reflects the image members alone.
  const selectedForBatch = filteredTemplates.filter((t) => selectedIds.has(t.id));
  const selectedVideoCount = selectedForBatch.filter(isVideoAdSource).length;
  // v6.51 — a video whose scene analysis hasn't landed can't be cloned yet
  // (prepare would just sit there). Keep it selectable so the bar explains
  // itself, but hold the button.
  const analyzingSelected = selectedForBatch.filter(isAwaitingAnalysis);
  const isAwaitingSelection = analyzingSelected.length > 0;
  const selectedImageCount = selectedForBatch.length - selectedVideoCount;
  const isSingleVideoSelection = selectedVideoCount === 1 && selectedImageCount === 0;
  // v7.04 — any video in the selection locks the batch button for non-admins.
  const videoLockedSelection = selectedVideoCount > 0 && !videoEnabled;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #DCE3ED', padding: '10px 20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#5F6D82', pointerEvents: 'none' }} />
            <input data-testid="input-canvas-search-templates" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search keywords, niches, styles…" style={{ width: '100%', padding: '7px 32px 7px 32px', borderRadius: 20, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 13, color: '#1A1A1A', outline: 'none', boxSizing: 'border-box' }} onFocus={(e) => (e.currentTarget.style.borderColor = '#94A3B8')} onBlur={(e) => (e.currentTarget.style.borderColor = '#DCE3ED')} />
            {searchQuery && <button style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }} onClick={() => setSearchQuery('')}><X style={{ width: 13, height: 13, color: '#5F6D82' }} /></button>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', position: 'relative' }}>
            <button data-testid="button-canvas-add-filter" onClick={() => { setShowFilterDropdown((v) => !v); setShowBrandDropdown(false); setShowSortDropdown(false); }} style={templateFilterTriggerStyle} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}>
              <Filter style={{ width: 12, height: 12 }} />Add Filter
            </button>
            {showFilterDropdown && <TemplateFilterDropdown available={scannedAds ? {Niche:[...new Set(templates.flatMap(t=>t.niche || []))],Format:[...new Set(templates.map(t=>t.format).filter(Boolean))],Style:[...new Set(templates.map(t=>adStyleOf(t)).filter(Boolean))],'Ad angles':[...new Set(templates.map(t=>(t as DiscoverCompetitor).adAngle || '').filter(Boolean))]} : undefined} activeFilters={activeFilters} onAdd={addFilter} onClose={() => setShowFilterDropdown(false)} />}
            <select aria-label="Media type" data-testid="template-media-filter" value={mediaFilter} onChange={event => { setMediaFilter(event.target.value); clearSelection(); }} style={templateFilterTriggerStyle}>
              <option value="all">All media</option><option value="image">Images</option><option value="video">Videos</option>
            </select>
            {scannedAds ? <select aria-label="Brands" data-testid={myAds ? "my-ads-brand-filter" : "competitor-brand-filter"} value={templateCollection} onChange={event=>{setTemplateCollection(event.target.value);clearSelection();}} style={templateFilterTriggerStyle}><option value="all">Show all</option>{Array.from(new Map(templates.map(t=>[myAds ? String((t as DiscoverMyAd).sourceBrandId) : (t as DiscoverCompetitor).advertiserName, (t as DiscoverCompetitor).advertiserName])).entries()).sort((a,b)=>a[1].localeCompare(b[1])).map(([id,name])=><option key={id} value={id}>{name}</option>)}</select> : <TemplateBrandFilter open={showBrandDropdown} onOpenChange={(open) => { setShowBrandDropdown(open); if (open) { setShowFilterDropdown(false); setShowSortDropdown(false); } }} value={templateCollection} onChange={(value) => {
              setTemplateCollection(value);
              clearSelection();
              if (sortBy !== 'niche') setSortBy(value === 'all' ? 'shuffled' : 'newest');
            }} />}
            {activeFilters.map((filter) => (
              <span key={`${filter.category}-${filter.value}`} data-testid={`chip-filter-${filter.category.toLowerCase()}-${filter.value.toLowerCase().replace(/\s+/g, '-')}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20, background: '#EAF0F7', color: '#526074', fontSize: 11, fontWeight: 500, border: '1px solid #DCE3ED' }}>
                <span style={{ fontSize: 9, color: '#5F6D82', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{filter.category}</span>
                {filter.label}
                <button onClick={() => removeFilter(filter.category, filter.value)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#5F6D82' }}><X style={{ width: 10, height: 10 }} /></button>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setShowSortDropdown((v) => !v); setShowFilterDropdown(false); setShowBrandDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, border: '1px solid transparent', background: 'transparent', color: '#526074', fontSize: 12, fontWeight: 500, cursor: 'pointer' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}>
                <ArrowUpDown style={{ width: 12, height: 12 }} />{sortLabels[sortBy]}
              </button>
              {showSortDropdown && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200, background: '#FFFFFF', border: '1px solid #DCE3ED', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', padding: '4px 0', minWidth: 130 }}>
                  {(Object.entries(sortLabels) as [SortOption, string][]).map(([opt, label]) => (
                    <button key={opt} onClick={() => { setSortBy(opt); setShowSortDropdown(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', fontSize: 12.5, color: '#334155', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}>
                      <span>{label}</span>
                      {sortBy === opt && <Check style={{ width: 12, height: 12, color: '#1A1A1A' }} />}
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Grid */}
      <div ref={gridScrollRef} data-testid="ad-library-scroll" data-library-scroll style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }} onClick={() => { setShowFilterDropdown(false); setShowSortDropdown(false); }}>
        <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: '#1A1A1A' }} data-testid="text-canvas-template-count">
          {filteredTemplates.length} {scannedAds ? 'Ad' : 'Template'}{filteredTemplates.length !== 1 ? 's' : ''}
          {activeFilters.length > 0 && <span style={{ fontWeight: 400, color: '#5F6D82' }}> (filtered)</span>}
        </div>
        {isLoading ? (
          <Masonry breakpointCols={templateMasonryBreakpoints} className="canvas-masonry-grid" columnClassName="canvas-masonry-column">
            {Array.from({ length: 12 }, (_, index) => <div key={index} aria-hidden="true" data-testid="library-loading-tile" className="library-media" style={{ aspectRatio: libraryMediaRatio(index % 3 === 0 ? 'portrait' : 'square'), borderRadius: 10, marginBottom: 12 }}><div className="library-media-placeholder" /></div>)}
          </Masonry>
        ) : filteredTemplates.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#EAF0F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {templates.length === 0 ? <Upload style={{ width: 22, height: 22, color: '#5F6D82' }} /> : <Search style={{ width: 22, height: 22, color: '#5F6D82' }} />}
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>{isError ? 'Couldn’t load ads' : myAds ? (templates.length === 0 ? 'No brand ads yet' : 'No brand ads found') : competitors ? (templates.length === 0 ? 'No saved competitor ads yet' : 'No competitor ads found') : templates.length === 0 ? 'No templates yet' : 'No templates found'}</p>
              <p style={{ fontSize: 13, color: '#5F6D82', marginTop: 4 }}>{isError ? 'Try loading your saved ads again.' : templates.length === 0 ? (myAds ? 'Scan your brand’s Ad Intelligence to bring its existing image ads here.' : competitors ? 'Saved competitor scans appear here. View ads from your brand’s Competitors section.' : 'Upload your first ad template to get started.') : 'Try different search terms or adjust your filters.'}</p>
            </div>
            {isError && <button onClick={()=>void refetch()} style={templateFilterTriggerStyle}>Try again</button>}
            {!scannedAds && templates.length === 0 && (
              <button onClick={onShowUpload} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: '#1A1A1A', color: '#FFFFFF', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Upload style={{ width: 14, height: 14 }} />Upload Template
              </button>
            )}
          </div>
        ) : (
          <Masonry breakpointCols={templateMasonryBreakpoints} className="canvas-masonry-grid" columnClassName="canvas-masonry-column">
            {filteredTemplates.slice(0, visibleCount).map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onClick={() => setSelectedTemplate(template)}
                onDelete={() => { if (confirm('Delete this template?')) deleteMutation.mutate(template.id); }}
                isSelected={selectedIds.has(template.id)}
                onToggleSelect={() => toggleSelectId(template.id)}
                selectionDisabled={!selectedIds.has(template.id) && selectedIds.size >= SELECT_CAP}
              />
            ))}
          </Masonry>
        )}
        {/* v2.29 — infinite-scroll sentinel; observed by the effect above to
            grow the window. Only rendered while more remain. */}
        {!isLoading && visibleCount < filteredTemplates.length && (
          <div ref={loadMoreRef} style={{ height: 1 }} aria-hidden="true" />
        )}
      </div>

      {selectedTemplate && (
        <TemplateDetailModal template={selectedTemplate} onClose={() => setSelectedTemplate(null)} onClone={() => handleClone(selectedTemplate)} onDelete={() => { if (confirm('Delete this template?')) deleteMutation.mutate(selectedTemplate.id); }} onUseAsReference={() => handleUseAsReference(selectedTemplate)} />
      )}
      {importSources && (
        <CloneDestinationPicker
          mode="import-to-canvas"
          imageUrl={importSources[0].imageUrl}
          imageName={importSources[0].name}
          templates={importSources}
          onPicked={async (projectId) => {
            await importDiscoveryImages(projectId, importSources);
            const store = useCanvasStore.getState();
            await store.switchProject(projectId);
            await store.refreshProjectImages(projectId);
            const importedUrls = new Set(importSources.map(source => source.imageUrl));
            const images = useCanvasStore.getState().projects[projectId]?.images ?? [];
            const imported = images.filter(image => importedUrls.has(image.src));
            store.setSelectedImageIds(imported.map(image => image.id));
            if (imported.length) store._writeProjectView(projectId, {pendingFitToRow: {
              minX: Math.min(...imported.map(image => image.x)),
              minY: Math.min(...imported.map(image => image.y)),
              maxX: Math.max(...imported.map(image => image.x + image.displayWidth)),
              maxY: Math.max(...imported.map(image => image.y + image.displayHeight)),
            }});
            store.setCanvasOpen(true);
            store.setActiveView('chat-canvas');
            clearSelection();
            toast({title: `Added ${importSources.length} image${importSources.length === 1 ? '' : 's'} to canvas`});
          }}
          onClose={() => setImportSources(null)}
        />
      )}
      {clonePickerTemplate && (
        <CloneDestinationPicker
          imageUrl={clonePickerTemplate.imageUrl}
          imageName={clonePickerTemplate.name}
          {...(clonePickerTemplate.mediaType === 'video'
            ? {
                // Video templates: project selection ONLY (labels-only mode).
                // onPicked short-circuits the picker's image-drop handlers;
                // brand resolve + SSE prepare run in the shared runner helper
                // (same flow as canvas-ad-templates-view.tsx).
                mode: 'video-clone' as const,
                onPicked: (projectId: number, productId?: number) => {
                  const template = clonePickerTemplate;
                  // Defer a tick so the picker's onClose state update settles
                  // before the async prepare flow starts (batch-flow pattern).
                  // v5.40 — productId arrives from the picker's in-modal
                  // product step (multi-product brands).
                  setTimeout(() => { void (async () => {
                    let templateId = template.id;
                    if (scannedAds) {
                      const projectResponse = await apiRequest('GET', `/api/canvas/projects/${projectId}`);
                      const project = await projectResponse.json();
                      const response = await apiRequest('POST', `${galleryEndpoint}/${template.id}/save`, {brandId:project.brandId ?? project.brand_id});
                      templateId = (await response.json()).id;
                    }
                    await pickProjectAndPrepareVideoClone({ templateId, projectId, productId });
                  })().catch(error=>toast({title:'Video clone could not start',description:error.message,variant:'destructive'})); }, 0);
                },
              }
            : { mode: 'clone-to-canvas' as const, autoVariationsCount: 2 })}
          onClose={() => setClonePickerTemplate(null)}
        />
      )}

      {showBatchPicker && (() => {
        // mediaType filter mirrors runBatchCloneInto — a video id lingering in
        // the selection must not appear in the batch preview either.
        const selectedTemplates = filteredTemplates.filter((t) => selectedIds.has(t.id) && !isVideoAdSource(t));
        if (selectedTemplates.length === 0) return null;
        const first = selectedTemplates[0];
        return (
          <CloneDestinationPicker
            imageUrl={first.imageUrl}
            imageName={first.name}
            mode="clone-to-canvas"
            autoVariationsCount={2}
            templates={selectedTemplates.map((t) => ({ imageUrl: t.imageUrl, name: t.name }))}
            onPicked={(projectId, productId, productName) => {
              setShowBatchPicker(false);
              // Defer one tick so the picker's onClose state update settles
              // before we kick off the heavy batch work.
              setTimeout(() => { runBatchCloneInto(projectId, productId, productName); }, 0);
            }}
            onClose={() => setShowBatchPicker(false)}
          />
        );
      })()}

      {/* Floating batch action bar — shows when one or more templates are
          selected in multi-select mode. Fires the clone runner for each
          selected source in parallel. */}
      {selectedIds.size > 0 && (
        <div
          data-testid="templates-batch-bar"
          style={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            background: '#FFFFFF',
            borderTop: '1px solid #DCE3ED',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 30,
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1A1A1A' }}>
              {selectedIds.size} of {SELECT_CAP} template{selectedIds.size === 1 ? '' : 's'} selected
            </span>
            <button
              data-testid="templates-batch-clear"
              onClick={clearSelection}
              disabled={isBatchCloning}
              style={{ background: 'transparent', border: 'none', color: '#526074', fontSize: 12.5, cursor: isBatchCloning ? 'not-allowed' : 'pointer', padding: '4px 8px', textDecoration: 'underline' }}
            >
              Clear
            </button>
          </div>
          <div data-testid="templates-batch-actions" style={{display:'flex',alignItems:'center',gap:8}}>
          <button
            data-testid="templates-batch-add-to-canvas"
            onClick={() => {
              const sources = templates.filter(template => selectedIds.has(template.id));
              if (sources.length) setImportSources(sources);
            }}
            disabled={isBatchCloning}
            style={{display:'flex',alignItems:'center',gap:8,padding:'10px 18px',borderRadius:10,border:'1px solid #94A3B8',background:'transparent',color:'#334155',fontSize:13,fontWeight:600,cursor:'pointer'}}
          >
            <Plus style={{width:14,height:14}} /> Add to canvas
          </button>
          <button
            data-testid="templates-batch-clone"
            onClick={handleBatchClone}
            disabled={isBatchCloning || isAwaitingSelection || videoLockedSelection}
            title={videoLockedSelection ? videoGateCopy.long : isAwaitingSelection ? 'Still analyzing the source video — this takes a minute after upload.' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 10,
              border: 'none',
              background: isBatchCloning || isAwaitingSelection || videoLockedSelection ? '#C8D3E2' : '#1A1A1A',
              color: '#FFFFFF',
              fontSize: 13.5, fontWeight: 600,
              cursor: isBatchCloning || isAwaitingSelection || videoLockedSelection ? 'not-allowed' : 'pointer',
            }}
          >
            {videoLockedSelection ? (
              <><Video style={{ width: 14, height: 14 }} /> {videoGateCopy.short}</>
            ) : isAwaitingSelection ? (
              <><Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> Analyzing<AnimatedDots /></>
            ) : isBatchCloning ? (
              <><Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> Cloning {selectedIds.size}…</>
            ) : isSingleVideoSelection ? (
              <><Video style={{ width: 14, height: 14 }} /> Clone Video Ad</>
            ) : (
              <><Copy style={{ width: 14, height: 14 }} /> Clone {selectedIds.size} Ad{selectedIds.size === 1 ? '' : 's'}{selectedImageCount > 0 ? ` (${selectedImageCount * 2} clones)` : ''}</>
            )}
          </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Find Ads Tab ─────────────────────────────────────────────────────────────
function AdCreativeImage({ snapshotUrl, alt = 'Ad creative', onImageExtracted }: { snapshotUrl: string; alt?: string; onImageExtracted?: (url: string) => void }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!snapshotUrl || fetchedRef.current) return;
    fetchedRef.current = true;
    fetch(`/api/ads/extract-image?url=${encodeURIComponent(snapshotUrl)}`).then((res) => {
      if (!res.ok) throw new Error('Failed');
      return res.json();
    }).then((data) => {
      setImageUrl(data.image_url); setLoading(false);
      if (data.image_url && onImageExtracted) onImageExtracted(data.image_url);
    }).catch(() => { setFailed(true); setLoading(false); });
  }, [snapshotUrl]);

  if (loading) return <div style={{ background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 0' }}><Loader2 style={{ width: 18, height: 18, color: '#C8D3E2' }} className="animate-spin" /></div>;
  if (failed || !imageUrl) return null;
  return <div style={{ position: 'relative', background: '#F1F5F9' }}><img src={imageUrl} alt={alt} style={{ width: '100%' }} loading="lazy" referrerPolicy="no-referrer" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /></div>;
}

function FindAdCard({ ad, onClick, onSave, showDetails }: { ad: SearchResult; onClick: () => void; onSave: () => void; showDetails: boolean }) {
  const { toast } = useToast();
  const daysAgo = formatDaysAgo(ad.ad_start_date_raw || ad.ad_start_date);
  const bodyText = cleanTemplateText(ad.ad_creative_body);
  const [expanded, setExpanded] = useState(false);
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string | null>(null);

  const isImageAd = ad.media_type !== 'video';
  const directImageUrl = (ad.media_url && !isSnapshotUrl(ad.media_url)) ? ad.media_url : null;
  const imageUrlForSave = directImageUrl || resolvedImageUrl;
  // Video ads: media_url IS the mp4 when it's a direct CDN url (same check
  // the HoverVideoCard render path uses below).
  const videoUrlForSave = (!isImageAd && ad.media_url && !isSnapshotUrl(ad.media_url)) ? ad.media_url : null;

  return (
    <div data-testid={`card-search-ad-${ad.ad_id}`} style={{ position: 'relative', maxWidth: showDetails ? undefined : 560, marginBottom: 12, background: '#FFFFFF', borderRadius: showDetails ? 12 : 10, border: '1px solid #DCE3ED', overflow: 'hidden', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }} onClick={onClick}>
      {showDetails && <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#EAF0F7', flexShrink: 0, overflow: 'hidden', border: '1px solid #DCE3ED' }}>
          {ad.page_logo_url ? <img src={ad.page_logo_url} alt={ad.page_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#526074' }}>{ad.page_name.charAt(0)}</div>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{ad.page_name}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {daysAgo && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#526074' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#5E8E6E', display: 'inline-block' }} />
              {daysAgo}
            </span>
          )}
          <button aria-label="Save ad to board" data-testid={`button-save-ad-${ad.ad_id}`} style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5F6D82', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 6 }} onClick={(e) => { e.stopPropagation(); onSave(); }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}>
            <Bookmark style={{ width: 13, height: 13 }} />
          </button>
        </div>
      </div>}

      {showDetails && bodyText && (
        <div style={{ padding: '0 14px 8px' }}>
          <p style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.5, margin: 0 }}>{expanded ? bodyText : truncateText(bodyText, 180)}</p>
          {bodyText.length > 180 && <button style={{ fontSize: 11, fontWeight: 600, color: '#1A1A1A', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 2 }} onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>{expanded ? 'Show Less' : 'Read More'}</button>}
        </div>
      )}

      {showDetails && ad.ad_creative_link_description && (
        <div style={{ padding: '0 14px 8px' }}>
          <p style={{ fontSize: 11.5, color: '#5F6D82', lineHeight: 1.4, margin: 0 }}>{truncateText(cleanTemplateText(ad.ad_creative_link_description), 120)}</p>
          {ad.cta_type && <span style={{ display: 'inline-block', marginTop: 6, padding: '4px 10px', border: '1px solid #C8D3E2', borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#334155' }}>{ad.cta_type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</span>}
        </div>
      )}

      {(ad.media_url && !isSnapshotUrl(ad.media_url)) ? (
        ad.media_type === 'video' ? <HoverVideoCard testId={`hover-video-${ad.ad_id}`} videoUrl={ad.media_url} thumbnailUrl={ad.media_thumbnail_url} alt={`${ad.page_name} video ad`} /> : <LibraryMedia src={ad.media_url} alt="Ad creative" />
      ) : ad.snapshot_url ? (
        <AdCreativeImage snapshotUrl={ad.snapshot_url} alt={`${ad.page_name} ad creative`} onImageExtracted={(url) => setResolvedImageUrl(url)} />
      ) : null}

      {!showDetails && <button aria-label="Save ad to board" data-testid={`button-save-ad-${ad.ad_id}`} style={{ ...templateCardActionStyle, position: 'absolute', top: 8, right: 8, zIndex: 2 }} onClick={(e) => { e.stopPropagation(); onSave(); }}><Bookmark style={{ width: 14, height: 14 }} /></button>}

      {isImageAd && imageUrlForSave && (
        <div style={showDetails ? { padding: '8px 14px', borderTop: '1px solid #EAF0F7' } : { position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
          <SaveTemplateBrandMenu testId={`button-save-template-${ad.ad_id}`} iconOnly={!showDetails} style={showDetails ? undefined : templateCardActionStyle} source={{ imageUrl: imageUrlForSave }} className="hover:!bg-[#F1F5F9]" />
        </div>
      )}

      {/* v6.57 — video GA: Save Video (video → cloneable template) open to all users */}
      {!isImageAd && videoUrlForSave && (
        <div style={showDetails ? { padding: '8px 14px', borderTop: '1px solid #EAF0F7' } : { position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
          <SaveTemplateBrandMenu testId={`button-save-video-template-${ad.ad_id}`} iconOnly={!showDetails} style={showDetails ? undefined : templateCardActionStyle} source={{ videoUrl: videoUrlForSave, thumbnailUrl: ad.media_thumbnail_url, name: `${ad.page_name} video ad` }} className="hover:!bg-[#F1F5F9]" />
        </div>
      )}
    </div>
  );
}

const findAdsMasonryBreakpoints = { default: 3, 1400: 3, 1100: 2, 700: 1 };

function FindAdsTab() {
  const [showDetails, setShowDetails] = useState(() => {
    try { return localStorage.getItem('admoji_find_ads_details') !== '0'; }
    catch { return true; }
  });
  const updateShowDetails = (show: boolean) => {
    setShowDetails(show);
    try { localStorage.setItem('admoji_find_ads_details', show ? '1' : '0'); }
    catch { /* Storage unavailable: retain preference for this session. */ }
  };
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchCursor, setSearchCursor] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSampleData, setIsSampleData] = useState(false);
  const [pageSuggestions, setPageSuggestions] = useState<Array<{ page_id: string; page_name: string; page_logo_url: string; ad_count?: number }>>([]);
  const [showPageSuggestions, setShowPageSuggestions] = useState(false);
  const [isSearchingPages, setIsSearchingPages] = useState(false);
  const [selectedPage, setSelectedPage] = useState<{ page_id: string; page_name: string; page_logo_url: string } | null>(null);
  const pageSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoLoadedRef = useRef(false);
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterCountry, setFilterCountry] = useState('US');
  const [filterMediaType, setFilterMediaType] = useState('all'); // v3.92 — 'all' | 'image' | 'video'
  const [sortBy, setSortBy] = useState<FindAdsSortOption>('newest');
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [mediaTypeOpen, setMediaTypeOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<SearchResult | null>(null);

  const sortedResults = useMemo(() => {
    const sorted = [...searchResults];
    if (sortBy === 'newest') sorted.sort((a, b) => new Date(b.ad_start_date_raw || b.ad_start_date || 0).getTime() - new Date(a.ad_start_date_raw || a.ad_start_date || 0).getTime());
    else if (sortBy === 'oldest') sorted.sort((a, b) => new Date(a.ad_start_date_raw || a.ad_start_date || 0).getTime() - new Date(b.ad_start_date_raw || b.ad_start_date || 0).getTime());
    else if (sortBy === 'longest_running') sorted.sort((a, b) => calculateDaysRunning(b.ad_start_date_raw || b.ad_start_date) - calculateDaysRunning(a.ad_start_date_raw || a.ad_start_date));
    return sorted;
  }, [searchResults, sortBy]);

  // Default browse feed (q='*') under the CURRENT filters — used by the
  // initial auto-load and by live filter changes when no query/page is set.
  const browseSearch = useCallback(async () => {
    setIsSearching(true); setHasSearched(true); setSearchError(null); setSearchResults([]); setSearchCursor(null); setIsSampleData(false);
    try {
      const params = new URLSearchParams({ country: filterCountry, status: filterStatus, media_type: filterMediaType, platform: 'all', limit: '25', q: '*' });
      const rangeStart = getDateFromPreset(datePreset);
      if (rangeStart) { params.set('delivery_date_min', formatDateForApi(rangeStart)); params.set('delivery_date_max', formatDateForApi(new Date())); }
      const res = await fetch(`/api/ads/search?${params.toString()}`);
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setSearchResults(data.ads || []); setHasMore(!!data.has_more); setSearchCursor(data.cursor); setIsSampleData(!!data.is_sample);
    } catch (err: any) { setSearchError(err.message); } finally { setIsSearching(false); }
  }, [filterCountry, filterStatus, filterMediaType, datePreset]);

  useEffect(() => {
    if (autoLoadedRef.current) return;
    autoLoadedRef.current = true;
    void browseSearch();
  }, [browseSearch]);

  // v5.27 — filters apply IMMEDIATELY (Dan: switching Media to Video did
  // nothing until Search was pressed): any server-side filter change
  // re-fires whatever is currently on screen — the active page/keyword
  // search when one is set, otherwise the browse feed. Client-side sorts
  // (newest/oldest/longest-running) never refetch; Top Impressions is a
  // server search_type, so entering/leaving it does. Deps are ONLY the
  // filter values — the effect closure is from the render that changed
  // them, so handleSearch/browseSearch/selectedPage/searchQuery are fresh.
  const filtersInitRef = useRef(false);
  const prevSortRef = useRef(sortBy);
  useEffect(() => {
    const sortChanged = prevSortRef.current !== sortBy;
    const sortNeedsRefetch = sortChanged && (sortBy === 'top_impressions' || prevSortRef.current === 'top_impressions');
    prevSortRef.current = sortBy;
    if (!filtersInitRef.current) { filtersInitRef.current = true; return; }
    if (sortChanged && !sortNeedsRefetch) return;
    if (selectedPage || searchQuery.trim()) void handleSearch();
    else void browseSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMediaType, filterStatus, filterCountry, datePreset, sortBy]);

  const fetchPageSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) { setPageSuggestions([]); setShowPageSuggestions(false); return; }
    setIsSearchingPages(true);
    try {
      const res = await fetch(`/api/pages/search?q=${encodeURIComponent(query)}&country=${filterCountry}`);
      if (res.ok) { const data = await res.json(); setPageSuggestions(data.pages || []); setShowPageSuggestions(true); }
    } catch {} finally { setIsSearchingPages(false); }
  }, [filterCountry]);

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchQuery(value); setSelectedPage(null);
    if (pageSearchDebounceRef.current) clearTimeout(pageSearchDebounceRef.current);
    pageSearchDebounceRef.current = setTimeout(() => { fetchPageSuggestions(value); }, 400);
  }, [fetchPageSuggestions]);

  const handleSelectPage = useCallback((page: { page_id: string; page_name: string; page_logo_url: string }) => {
    setSelectedPage(page); setSearchQuery(page.page_name); setShowPageSuggestions(false); setPageSuggestions([]);
    (async () => {
      setIsSearching(true); setSearchError(null); setSearchResults([]); setSearchCursor(null); setHasSearched(true); setIsSampleData(false);
      try {
        const params = new URLSearchParams({ page_ids: page.page_id, country: filterCountry, status: filterStatus, media_type: filterMediaType, platform: 'all', limit: '25' });
        const rangeStart = getDateFromPreset(datePreset);
        if (rangeStart) { params.set('delivery_date_min', formatDateForApi(rangeStart)); params.set('delivery_date_max', formatDateForApi(new Date())); }
        const res = await fetch(`/api/ads/search?${params.toString()}`);
        if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Search failed'); }
        const data = await res.json();
        setSearchResults(data.ads || []); setHasMore(data.has_more); setSearchCursor(data.cursor); setIsSampleData(!!data.is_sample);
      } catch (err: any) { setSearchError(err.message); } finally { setIsSearching(false); }
    })();
  }, [filterCountry, filterStatus, filterMediaType, datePreset]);

  const handleSearch = useCallback(async (append = false) => {
    if (!searchQuery.trim() && !selectedPage) return;
    setIsSearching(true); setSearchError(null); setShowPageSuggestions(false);
    if (!append) { setSearchResults([]); setSearchCursor(null); setHasSearched(true); setIsSampleData(false); }
    try {
      const params = new URLSearchParams({ country: filterCountry, status: filterStatus, media_type: filterMediaType, platform: 'all', limit: '25' });
      if (sortBy === 'top_impressions') params.set('search_type', 'top_impressions');
      const rangeStart = getDateFromPreset(datePreset);
      if (rangeStart) { params.set('delivery_date_min', formatDateForApi(rangeStart)); params.set('delivery_date_max', formatDateForApi(new Date())); }
      if (selectedPage) params.set('page_ids', selectedPage.page_id); else params.set('q', searchQuery.trim());
      if (append && searchCursor) params.set('after', searchCursor);
      const res = await fetch(`/api/ads/search?${params.toString()}`);
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Search failed'); }
      const data = await res.json();
      setSearchResults(prev => append ? [...prev, ...data.ads] : data.ads); setHasMore(data.has_more); setSearchCursor(data.cursor); setIsSampleData(!!data.is_sample);
    } catch (err: any) { setSearchError(err.message); } finally { setIsSearching(false); }
  }, [searchQuery, selectedPage, filterCountry, filterStatus, filterMediaType, datePreset, searchCursor, sortBy]);

  const COUNTRY_FLAGS: Record<string, string> = { US: '🇺🇸', GB: '🇬🇧', CA: '🇨🇦', AU: '🇦🇺', DE: '🇩🇪', FR: '🇫🇷', BR: '🇧🇷', ES: '🇪🇸', IT: '🇮🇹', NL: '🇳🇱', JP: '🇯🇵', IN: '🇮🇳', MX: '🇲🇽' };
  const countryLabel = (COUNTRY_FLAGS[filterCountry] || '🌍') + ' ' + filterCountry;
  const sortLabels: Record<FindAdsSortOption, string> = { newest: 'Newest', oldest: 'Oldest', longest_running: 'Longest Running', top_impressions: 'Top Impressions' };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search bar */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #DCE3ED', padding: '10px 20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          {selectedPage && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: '#EAF0F7', borderRadius: 20, border: '1px solid #DCE3ED', flexShrink: 0 }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#DCE3ED', overflow: 'hidden', flexShrink: 0 }}>
                {selectedPage.page_logo_url ? <img src={selectedPage.page_logo_url} alt={selectedPage.page_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#526074' }}>{selectedPage.page_name.charAt(0)}</div>}
              </div>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#334155', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedPage.page_name}</span>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#5F6D82' }} onClick={() => { setSelectedPage(null); setSearchQuery(''); setSearchResults([]); setHasSearched(false); }}><X style={{ width: 11, height: 11 }} /></button>
            </div>
          )}
          <div style={{ position: 'relative', flex: 1 }}>
            <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#5F6D82', pointerEvents: 'none', zIndex: 1 }} />
            <input data-testid="input-search-ads" placeholder={selectedPage ? `Search keywords in ${selectedPage.page_name}...` : 'Search keywords, brands, categories...'} style={{ width: '100%', padding: '7px 36px 7px 32px', borderRadius: 20, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 13, color: '#1A1A1A', outline: 'none', boxSizing: 'border-box' }} value={searchQuery} onChange={(e) => handleSearchInputChange(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setShowPageSuggestions(false); handleSearch(); } if (e.key === 'Escape') setShowPageSuggestions(false); }} onFocus={() => { if (pageSuggestions.length > 0) setShowPageSuggestions(true); }} onBlur={() => { setTimeout(() => setShowPageSuggestions(false), 200); }} />
            {showPageSuggestions && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#FFFFFF', border: '1px solid #DCE3ED', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, maxHeight: 280, overflowY: 'auto' }}>
                {pageSuggestions.length > 0 && <div style={{ padding: '6px 12px 4px', fontSize: 10, fontWeight: 600, color: '#5F6D82', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Advertisers</div>}
                {pageSuggestions.map((page) => (
                  <button key={page.page_id} data-testid={`button-page-suggestion-${page.page_id}`} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }} onMouseDown={(e) => { e.preventDefault(); handleSelectPage(page); }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#EAF0F7', flexShrink: 0, overflow: 'hidden', border: '1px solid #DCE3ED' }}>
                      {page.page_logo_url ? <img src={page.page_logo_url} alt={page.page_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#526074' }}>{page.page_name.charAt(0)}</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.page_name}</p>
                    </div>
                    <ExternalLink style={{ width: 11, height: 11, color: '#C8D3E2', flexShrink: 0 }} />
                  </button>
                ))}
                {isSearchingPages && <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 6, color: '#5F6D82' }}><Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /><span style={{ fontSize: 12 }}>Searching advertisers...</span></div>}
                {!isSearchingPages && pageSuggestions.length === 0 && searchQuery.length >= 2 && <div style={{ padding: '10px 12px', fontSize: 12, color: '#5F6D82', textAlign: 'center' }}>No advertisers found. Press Enter to search ad content.</div>}
              </div>
            )}
          </div>
          <button data-testid="button-search-submit" onClick={() => { setShowPageSuggestions(false); handleSearch(); }} disabled={isSearching || (!searchQuery.trim() && !selectedPage)} style={{ padding: '7px 16px', borderRadius: 20, border: 'none', background: isSearching || (!searchQuery.trim() && !selectedPage) ? '#C8D3E2' : '#1A1A1A', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: isSearching || (!searchQuery.trim() && !selectedPage) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {isSearching ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> : null}
            Search
          </button>
        </div>

        {/* Filter row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {/* Country */}
          <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 12, color: '#334155', cursor: 'pointer', outline: 'none' }}>
            <option value="US">🇺🇸 US</option>
            <option value="GB">🇬🇧 UK</option>
            <option value="CA">🇨🇦 CA</option>
            <option value="AU">🇦🇺 AU</option>
            <option value="DE">🇩🇪 DE</option>
            <option value="FR">🇫🇷 FR</option>
            <option value="BR">🇧🇷 BR</option>
            <option value="IN">🇮🇳 IN</option>
          </select>

          {/* Status */}
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 12, color: '#334155', cursor: 'pointer', outline: 'none' }}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All Statuses</option>
          </select>

          {/* Date range */}
          <div style={{ position: 'relative' }}>
            <button data-testid="button-date-range" onClick={() => setDatePickerOpen(!datePickerOpen)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 12, color: '#334155', cursor: 'pointer' }}>
              <Calendar style={{ width: 11, height: 11 }} />{getPresetLabel(datePreset)}
            </button>
            {datePickerOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setDatePickerOpen(false)} />
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#FFFFFF', border: '1px solid #DCE3ED', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, padding: '4px 0', minWidth: 160 }}>
                  {(['7d', '14d', '30d', '90d', '365d', 'all'] as DatePreset[]).map(preset => (
                    <button key={preset} data-testid={`button-date-preset-${preset}`} onClick={() => { setDatePreset(preset); setDatePickerOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', fontSize: 12.5, color: '#334155', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}>
                      {getPresetLabel(preset)}
                      {datePreset === preset && <Check style={{ width: 11, height: 11, color: '#1A1A1A' }} />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Sort */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setSortOpen(!sortOpen)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 12, color: '#334155', cursor: 'pointer' }}>
              <ArrowUpDown style={{ width: 11, height: 11 }} />{sortLabels[sortBy]}
            </button>
            {sortOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setSortOpen(false)} />
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#FFFFFF', border: '1px solid #DCE3ED', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, padding: '4px 0', minWidth: 160 }}>
                  {(Object.entries(sortLabels) as [FindAdsSortOption, string][]).map(([opt, label]) => (
                    <button key={opt} onClick={() => { setSortBy(opt); setSortOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', fontSize: 12.5, color: '#334155', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}>
                      {label}
                      {sortBy === opt && <Check style={{ width: 11, height: 11, color: '#1A1A1A' }} />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* v3.92 — Media type filter (Image / Video / All). This embedded
              Find-ads tab previously hardcoded media_type='all' with no UI. */}
          <div style={{ position: 'relative' }}>
            <button data-testid="button-media-type" onClick={() => setMediaTypeOpen(!mediaTypeOpen)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 12, color: '#334155', cursor: 'pointer' }}>
              {filterMediaType === 'video' ? <Video style={{ width: 11, height: 11 }} /> : <Image style={{ width: 11, height: 11 }} />}
              {filterMediaType === 'image' ? 'Image' : filterMediaType === 'video' ? 'Video' : 'All Media'}
            </button>
            {mediaTypeOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setMediaTypeOpen(false)} />
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#FFFFFF', border: '1px solid #DCE3ED', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, padding: '4px 0', minWidth: 150 }}>
                  {([['all', 'All Media'], ['image', 'Image'], ['video', 'Video']] as [string, string][]).map(([val, label]) => (
                    <button key={val} data-testid={`media-type-${val}`} onClick={() => { setFilterMediaType(val); setMediaTypeOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', fontSize: 12.5, color: '#334155', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        {val === 'video' ? <Video style={{ width: 11, height: 11, color: '#5F6D82' }} /> : val === 'image' ? <Image style={{ width: 11, height: 11, color: '#5F6D82' }} /> : null}
                        {label}
                      </span>
                      {filterMediaType === val && <Check style={{ width: 11, height: 11, color: '#1A1A1A' }} />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <label htmlFor="find-ads-show-details" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#334155', cursor: 'pointer', minHeight: 32, whiteSpace: 'nowrap' }}>
            Show ad details
            <Switch id="find-ads-show-details" checked={showDetails} onCheckedChange={updateShowDetails} />
          </label>
        </div>
      </div>

      {/* Results */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: 'transparent' }}>
        {isSampleData && (
          <div style={{ marginBottom: 12, padding: '8px 14px', background: '#FEFCE8', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 12, color: '#92400E', display: 'flex', alignItems: 'center', gap: 6 }}>
            Sample data — configure SearchAPI.io to see live ads.
          </div>
        )}
        {isSearching && searchResults.length === 0 ? (
          <Masonry breakpointCols={showDetails ? findAdsMasonryBreakpoints : templateMasonryBreakpoints} className={showDetails ? "flex -ml-3 w-auto" : "canvas-masonry-grid"} columnClassName={showDetails ? "pl-3 bg-clip-padding" : "canvas-masonry-column"}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ marginBottom: 12, background: '#FFFFFF', borderRadius: 12, border: '1px solid #DCE3ED', overflow: 'hidden', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
                <div style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#DCE3ED' }} />
                  <div style={{ flex: 1 }}><div style={{ height: 10, background: '#DCE3ED', borderRadius: 4, width: '60%' }} /></div>
                </div>
                <div style={{ padding: '0 12px 10px' }}><div style={{ height: 8, background: '#DCE3ED', borderRadius: 4, marginBottom: 6 }} /><div style={{ height: 8, background: '#DCE3ED', borderRadius: 4, width: '80%' }} /></div>
                <div style={{ height: i % 3 === 0 ? 160 : i % 3 === 1 ? 220 : 190, background: '#DCE3ED' }} />
              </div>
            ))}
          </Masonry>
        ) : searchError ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12, textAlign: 'center' }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X style={{ width: 20, height: 20, color: '#DC2626' }} /></div>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>Search Error</p>
            <p style={{ fontSize: 12.5, color: '#5F6D82' }}>{searchError}</p>
            <button onClick={() => handleSearch()} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #DCE3ED', background: '#FFFFFF', color: '#334155', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }}>Try Again</button>
          </div>
        ) : searchResults.length === 0 && hasSearched ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#EAF0F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Search style={{ width: 20, height: 20, color: '#5F6D82' }} /></div>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>No ads found</p>
            <p style={{ fontSize: 12.5, color: '#5F6D82' }}>Try different keywords or adjust your filters.</p>
          </div>
        ) : (
          <>
            <Masonry breakpointCols={showDetails ? findAdsMasonryBreakpoints : templateMasonryBreakpoints} className={showDetails ? "flex -ml-3 w-auto" : "canvas-masonry-grid"} columnClassName={showDetails ? "pl-3 bg-clip-padding" : "canvas-masonry-column"}>
              {sortedResults.map((ad) => (
                <FindAdCard key={ad.ad_id} ad={ad} showDetails={showDetails} onClick={() => setSelectedAd(ad)} onSave={() => toast({ title: 'Use the standalone Find Ads page to save to boards' })} />
              ))}
            </Masonry>
            {hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 16 }}>
                <button onClick={() => handleSearch(true)} disabled={isSearching} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #DCE3ED', background: '#FFFFFF', color: '#334155', fontSize: 13, fontWeight: 500, cursor: isSearching ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {isSearching && <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" />}Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Simple ad detail panel */}
      {selectedAd && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelectedAd(null)}>
          <div style={{ background: '#F7F9FC', border: '1px solid #DCE3ED', borderRadius: 16, width: '90%', maxWidth: 680, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #DCE3ED', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#EAF0F7', overflow: 'hidden', border: '1px solid #DCE3ED' }}>
                  {selectedAd.page_logo_url ? <img src={selectedAd.page_logo_url} alt={selectedAd.page_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#526074' }}>{selectedAd.page_name.charAt(0)}</div>}
                </div>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', margin: 0 }}>{selectedAd.page_name}</p>
                  <p style={{ fontSize: 11, color: '#5F6D82', margin: 0 }}>Sponsored · {selectedAd.platform}</p>
                </div>
              </div>
              <button onClick={() => setSelectedAd(null)} style={{ width: 28, height: 28, borderRadius: 8, background: 'transparent', color: '#5F6D82', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}><X style={{ width: 15, height: 15 }} /></button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {cleanTemplateText(selectedAd.ad_creative_body) && <p style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.6, margin: 0 }}>{cleanTemplateText(selectedAd.ad_creative_body)}</p>}
              {(selectedAd.media_url && !isSnapshotUrl(selectedAd.media_url)) ? (
                selectedAd.media_type === 'video' ? <HoverVideoCard testId={`hover-video-detail-${selectedAd.ad_id}`} videoUrl={selectedAd.media_url} thumbnailUrl={selectedAd.media_thumbnail_url} alt={`${selectedAd.page_name} video ad`} /> : <img src={selectedAd.media_url} alt="Ad creative" style={{ width: '100%', borderRadius: 10 }} referrerPolicy="no-referrer" />
              ) : selectedAd.snapshot_url ? <AdCreativeImage snapshotUrl={selectedAd.snapshot_url} alt="Ad creative" /> : null}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 11, color: '#5F6D82', fontWeight: 500 }}>Status</span>
                  <span style={{ fontSize: 13, color: selectedAd.status === 'ACTIVE' ? '#5E8E6E' : '#526074', fontWeight: 500 }}>{selectedAd.status}</span>
                </div>
                {selectedAd.ad_start_date && <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ fontSize: 11, color: '#5F6D82', fontWeight: 500 }}>Started</span><span style={{ fontSize: 13, color: '#334155' }}>{selectedAd.ad_start_date}</span></div>}
                {selectedAd.ad_start_date && <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ fontSize: 11, color: '#5F6D82', fontWeight: 500 }}>Running</span><span style={{ fontSize: 13, color: '#334155' }}>{calculateDaysRunning(selectedAd.ad_start_date)} days</span></div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Organic Posts Tab ────────────────────────────────────────────────────────
function HoverVideoPlayer({ videoUrl, thumbnailUrl, caption }: { videoUrl: string; thumbnailUrl: string; caption: string }) {
  const [isHovering, setIsHovering] = useState(false);
  const [embedLoaded, setEmbedLoaded] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoId = extractVideoId(videoUrl);

  const handleMouseEnter = () => { hoverTimer.current = setTimeout(() => setIsHovering(true), 400); };
  const handleMouseLeave = () => { if (hoverTimer.current) clearTimeout(hoverTimer.current); setIsHovering(false); setEmbedLoaded(false); };

  return (
    <div className="relative w-full aspect-[9/16]" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {thumbnailUrl ? (
        <img src={thumbnailUrl} alt={caption?.slice(0, 50) || 'TikTok post'} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 300ms', opacity: isHovering && embedLoaded ? 0 : 1 }} loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
      ) : (
        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #334155, #1A1A1A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Play style={{ width: 36, height: 36, color: 'rgba(255,255,255,0.4)' }} />
        </div>
      )}
      {isHovering && videoId && <iframe src={`https://www.tiktok.com/embed/v2/${videoId}?autoplay=1&muted=1&controls=0`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} allow="autoplay; encrypted-media" allowFullScreen onLoad={() => setEmbedLoaded(true)} />}
      {isHovering && !embedLoaded && videoId && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)' }}><Loader2 style={{ width: 20, height: 20, color: 'white' }} className="animate-spin" /></div>}
    </div>
  );
}

function SaveToTemplatesButton({ thumbnailUrl, testId }: { thumbnailUrl: string; testId: string }) {
  return <SaveTemplateBrandMenu source={{ imageUrl: thumbnailUrl }} testId={testId}
    style={{ padding: '8px 16px', borderRadius: 10, border: '1px solid #DCE3ED', background: '#FFFFFF', fontSize: 13 }} />;
}

function OrganicPostCard({ post, isSaved, onSave, onClick }: { post: TikTokSearchPost; isSaved: boolean; onSave: () => void; onClick: () => void }) {

  return (
    <div data-testid={`card-post-${post.externalId}`} style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', border: '1px solid #DCE3ED', cursor: 'pointer', marginBottom: 12 }} onClick={onClick} className="group">
      <div style={{ position: 'relative' }}>
        <HoverVideoPlayer videoUrl={post.videoUrl} thumbnailUrl={post.thumbnailUrl} caption={post.caption} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)', opacity: 0, transition: 'opacity 200ms', pointerEvents: 'none' }} className="group-hover:opacity-100" />
        <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6 }} className="opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <button data-testid={`button-save-${post.externalId}`} onClick={(e) => { e.stopPropagation(); onSave(); }} style={{ padding: 6, borderRadius: '50%', background: isSaved ? '#334155' : 'rgba(0,0,0,0.4)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bookmark style={{ width: 12, height: 12 }} fill={isSaved ? 'white' : 'none'} />
          </button>
          {post.videoUrl && (
            <a href={post.videoUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ padding: 6, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ExternalLink style={{ width: 12, height: 12 }} />
            </a>
          )}
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 10, background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)', zIndex: 1, pointerEvents: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'white', fontSize: 11 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Eye style={{ width: 11, height: 11 }} />{formatCount(post.views)}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Heart style={{ width: 11, height: 11 }} />{formatCount(post.likes)}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MessageCircle style={{ width: 11, height: 11 }} />{formatCount(post.comments)}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Share2 style={{ width: 11, height: 11 }} />{formatCount(post.shares)}</span>
          </div>
        </div>
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          {post.creatorAvatar ? <img src={post.creatorAvatar} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} /> : <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg, #5F6D82, #334155)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 9, fontWeight: 700 }}>{(post.creatorName || '?')[0]?.toUpperCase()}</div>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#1A1A1A', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.creatorName}</p>
            {post.creatorUsername && <p style={{ fontSize: 10, color: '#5F6D82', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{post.creatorUsername}</p>}
          </div>
        </div>
        {post.caption && <p style={{ fontSize: 11, color: '#526074', lineHeight: 1.4, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.caption}</p>}
        {post.thumbnailUrl && (
          <div style={{ borderTop: '1px solid #EAF0F7', marginTop: 6, paddingTop: 6 }}>
            <SaveTemplateBrandMenu source={{ imageUrl: post.thumbnailUrl }} testId={`button-save-template-${post.externalId}`} style={{ padding: '5px 0', fontSize: 11 }} />
          </div>
        )}
      </div>
    </div>
  );
}

function SavedOrganicPostCard({ post, onDelete, onToggleFavorite }: { post: OrganicPost; onDelete: () => void; onToggleFavorite: () => void }) {

  return (
    <div data-testid={`card-saved-post-${post.id}`} style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', border: '1px solid #DCE3ED', marginBottom: 12 }} className="group">
      <div style={{ position: 'relative' }}>
        <HoverVideoPlayer videoUrl={post.videoUrl || ''} thumbnailUrl={post.thumbnailUrl || ''} caption={post.caption || ''} />
        <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6 }} className="opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <button data-testid={`button-favorite-${post.id}`} onClick={onToggleFavorite} style={{ padding: 6, borderRadius: '50%', background: post.isFavorite ? '#DC2626' : 'rgba(0,0,0,0.4)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Heart style={{ width: 12, height: 12 }} fill={post.isFavorite ? 'white' : 'none'} />
          </button>
          {post.videoUrl && <a href={post.videoUrl} target="_blank" rel="noopener noreferrer" style={{ padding: 6, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ExternalLink style={{ width: 12, height: 12 }} /></a>}
          <button data-testid={`button-delete-${post.id}`} onClick={onDelete} style={{ padding: 6, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X style={{ width: 12, height: 12 }} /></button>
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 10, background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)', zIndex: 1, pointerEvents: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'white', fontSize: 11 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Eye style={{ width: 11, height: 11 }} />{formatCount(post.views || 0)}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Heart style={{ width: 11, height: 11 }} />{formatCount(post.likes || 0)}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MessageCircle style={{ width: 11, height: 11 }} />{formatCount(post.comments || 0)}</span>
          </div>
        </div>
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {post.creatorAvatar ? <img src={post.creatorAvatar} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} /> : <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg, #5F6D82, #334155)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 9, fontWeight: 700 }}>{(post.creatorName || '?')[0]?.toUpperCase()}</div>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#1A1A1A', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.creatorName}</p>
          </div>
        </div>
        {post.thumbnailUrl && (
          <div style={{ borderTop: '1px solid #EAF0F7', marginTop: 6, paddingTop: 6 }}>
            <SaveTemplateBrandMenu source={{ imageUrl: post.thumbnailUrl }} testId={`button-save-template-saved-${post.id}`} style={{ padding: '5px 0', fontSize: 11 }} />
          </div>
        )}
      </div>
    </div>
  );
}

const organicMasonryBreakpoints = { default: 4, 1280: 4, 1024: 3, 768: 2, 640: 1 };

function OrganicPostsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'discover' | 'search' | 'saved'>('discover');
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sortBy, setSortBy] = useState<OrganicSortOption>('views');
  const [filterType, setFilterType] = useState<OrganicFilterType>('all');
  const [searchResults, setSearchResults] = useState<TikTokSearchPost[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedPost, setSelectedPost] = useState<TikTokSearchPost | null>(null);
  const [sortOpen, setSortOpen] = useState(false);
  const refreshTriggeredRef = useRef(false);

  const categoryQueryParam = activeCategory === 'all' ? '' : `?category=${activeCategory}`;

  const { data: cachedPosts = [], isLoading: isCachedLoading } = useQuery<OrganicPost[]>({
    queryKey: ['/api/organic-posts', activeCategory],
    queryFn: async () => { const res = await fetch(`/api/organic-posts${categoryQueryParam}`); if (!res.ok) throw new Error('Failed to fetch posts'); return res.json(); },
  });

  const { data: savedPosts = [] } = useQuery<OrganicPost[]>({ queryKey: ['/api/organic-posts'] });

  const { data: stalenessData } = useQuery({
    queryKey: ['/api/organic-posts/staleness'],
    queryFn: async () => { const res = await fetch('/api/organic-posts/staleness'); if (!res.ok) throw new Error('Failed'); return res.json(); },
  });

  const triggerRefresh = useCallback(async (categories?: string[]) => {
    setIsRefreshing(true);
    try {
      const res = await apiRequest('POST', '/api/organic-posts/refresh', { categories: categories || [] });
      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ['/api/organic-posts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/organic-posts/staleness'] });
      toast({ title: 'Content refreshed', description: `${data.totalSaved} posts updated` });
    } catch (error: any) {
      toast({ title: 'Refresh failed', description: error.message, variant: 'destructive' });
    } finally { setIsRefreshing(false); }
  }, [queryClient, toast]);

  useEffect(() => {
    if (refreshTriggeredRef.current) return;
    if (!stalenessData) return;
    const staleCategories = stalenessData.staleCategories as string[] | undefined;
    if (staleCategories && staleCategories.length > 0) { refreshTriggeredRef.current = true; triggerRefresh(staleCategories); }
    else if (cachedPosts.length === 0 && !isCachedLoading) { refreshTriggeredRef.current = true; triggerRefresh(); }
  }, [stalenessData, cachedPosts.length, isCachedLoading, triggerRefresh]);

  const savedExternalIds = useMemo(() => new Set(savedPosts.map((p) => p.externalId).filter(Boolean)), [savedPosts]);

  const handleSearch = useCallback(async () => {
    if (!searchInput.trim()) return;
    setIsSearching(true); setSearchKeyword(searchInput.trim());
    try {
      const res = await fetch(`/api/tiktok/search?keyword=${encodeURIComponent(searchInput.trim())}&limit=30`);
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Search failed'); }
      const data = await res.json();
      setSearchResults(data.posts || []);
      if ((data.posts || []).length === 0) toast({ title: 'No results', description: 'Try a different keyword' });
    } catch (error: any) { toast({ title: 'Search failed', description: error.message, variant: 'destructive' }); }
    finally { setIsSearching(false); }
  }, [searchInput, toast]);

  const saveMutation = useMutation({
    mutationFn: async (post: TikTokSearchPost) => {
      const res = await apiRequest('POST', '/api/organic-posts', { externalId: post.externalId, platform: 'tiktok', creatorName: post.creatorName, creatorUsername: post.creatorUsername, creatorAvatar: post.creatorAvatar, caption: post.caption, videoUrl: post.videoUrl, thumbnailUrl: post.thumbnailUrl, musicName: post.musicName, views: post.views, likes: post.likes, comments: post.comments, shares: post.shares, hashtags: post.hashtags, isSponsored: post.isSponsored, postDate: post.postDate });
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['/api/organic-posts'] }); toast({ title: 'Post saved' }); },
    onError: (error: any) => { toast({ title: 'Save failed', description: error.message, variant: 'destructive' }); },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => { await apiRequest('DELETE', `/api/organic-posts/${id}`); },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['/api/organic-posts'] }); toast({ title: 'Post removed' }); },
  });

  const favoriteMutation = useMutation({
    mutationFn: async (id: number) => { const res = await apiRequest('PATCH', `/api/organic-posts/${id}/favorite`); return res.json(); },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['/api/organic-posts'] }); },
  });

  const sortFn = (a: any, b: any) => {
    switch (sortBy) {
      case 'views': return (b.views || 0) - (a.views || 0);
      case 'likes': return (b.likes || 0) - (a.likes || 0);
      case 'comments': return (b.comments || 0) - (a.comments || 0);
      case 'shares': return (b.shares || 0) - (a.shares || 0);
      case 'newest': return new Date(b.postDate || b.createdAt || 0).getTime() - new Date(a.postDate || a.createdAt || 0).getTime();
      default: return 0;
    }
  };

  const sortedCachedPosts = useMemo(() => {
    let filtered = [...cachedPosts];
    if (filterType === 'organic') filtered = filtered.filter((p) => !p.isSponsored);
    if (filterType === 'sponsored') filtered = filtered.filter((p) => p.isSponsored);
    return filtered.sort(sortFn);
  }, [cachedPosts, sortBy, filterType]);

  const sortedSearchResults = useMemo(() => {
    let filtered = [...searchResults];
    if (filterType === 'organic') filtered = filtered.filter((p) => !p.isSponsored);
    if (filterType === 'sponsored') filtered = filtered.filter((p) => p.isSponsored);
    return filtered.sort(sortFn);
  }, [searchResults, sortBy, filterType]);

  const sortedSavedPosts = useMemo(() => {
    let filtered = [...savedPosts];
    if (filterType === 'organic') filtered = filtered.filter((p) => !p.isSponsored);
    if (filterType === 'sponsored') filtered = filtered.filter((p) => p.isSponsored);
    return filtered.sort(sortFn);
  }, [savedPosts, sortBy, filterType]);

  const sortLabels: Record<OrganicSortOption, string> = { views: 'Most Views', likes: 'Most Likes', comments: 'Most Comments', shares: 'Most Shares', newest: 'Newest First' };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Controls */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #DCE3ED', padding: '10px 20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          {/* Sub-tabs */}
          <div style={{ display: 'flex', background: '#EAF0F7', borderRadius: 8, padding: 2 }}>
            {(['discover', 'search', 'saved'] as const).map((tab) => (
              <button key={tab} data-testid={`button-tab-${tab}`} onClick={() => setActiveTab(tab)} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12.5, fontWeight: activeTab === tab ? 600 : 400, color: activeTab === tab ? '#1A1A1A' : '#526074', background: activeTab === tab ? '#FFFFFF' : 'transparent', border: 'none', cursor: 'pointer', textTransform: 'capitalize', boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
                {tab === 'discover' ? <><TrendingUp style={{ width: 11, height: 11, display: 'inline', marginRight: 4 }} />Discover</> : tab === 'search' ? <><Search style={{ width: 11, height: 11, display: 'inline', marginRight: 4 }} />Search</> : <><Bookmark style={{ width: 11, height: 11, display: 'inline', marginRight: 4 }} />Saved ({savedPosts.length})</>}
              </button>
            ))}
          </div>

          <div style={{ flex: 1 }} />

          {/* Sort */}
          <div style={{ position: 'relative' }}>
            <button data-testid="button-sort" onClick={() => setSortOpen(!sortOpen)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 12, color: '#334155', cursor: 'pointer' }}>
              <ArrowUpDown style={{ width: 11, height: 11 }} />{sortLabels[sortBy]}
            </button>
            {sortOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setSortOpen(false)} />
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: '#FFFFFF', border: '1px solid #DCE3ED', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, padding: '4px 0', minWidth: 160 }}>
                  {(Object.entries(sortLabels) as [OrganicSortOption, string][]).map(([opt, label]) => (
                    <button key={opt} data-testid={`sort-${opt}`} onClick={() => { setSortBy(opt); setSortOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', fontSize: 12.5, color: '#334155', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}>
                      {label}{sortBy === opt && <Check style={{ width: 11, height: 11, color: '#1A1A1A' }} />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Category chips (discover tab only) */}
        {activeTab === 'discover' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {(Object.keys(CATEGORY_LABELS) as CategoryFilter[]).map((cat) => (
              <button key={cat} data-testid={`button-category-${cat}`} onClick={() => setActiveCategory(cat)} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, border: activeCategory === cat ? '1px solid #334155' : '1px solid #DCE3ED', background: activeCategory === cat ? '#334155' : '#FFFFFF', color: activeCategory === cat ? '#FFFFFF' : '#526074', cursor: 'pointer', transition: 'all 120ms' }}>
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <button data-testid="button-refresh" onClick={() => triggerRefresh(activeCategory === 'all' ? undefined : [activeCategory])} disabled={isRefreshing} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#FFFFFF', color: '#526074', fontSize: 12, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}>
              {isRefreshing ? <Loader2 style={{ width: 11, height: 11 }} className="animate-spin" /> : <TrendingUp style={{ width: 11, height: 11 }} />}
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        )}

        {/* Search input (search tab only) */}
        {activeTab === 'search' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#5F6D82', pointerEvents: 'none' }} />
              <input data-testid="input-search" placeholder="Search TikTok by keyword (e.g. skincare routine, protein shake)" style={{ width: '100%', padding: '7px 10px 7px 32px', borderRadius: 20, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 13, color: '#1A1A1A', outline: 'none', boxSizing: 'border-box' }} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
            </div>
            <button data-testid="button-search" onClick={handleSearch} disabled={isSearching || !searchInput.trim()} style={{ padding: '7px 16px', borderRadius: 20, border: 'none', background: isSearching || !searchInput.trim() ? '#C8D3E2' : '#1A1A1A', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: isSearching || !searchInput.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {isSearching ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <Search style={{ width: 13, height: 13 }} />}
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: 'transparent' }}>
        {activeTab === 'discover' && (
          <>
            {isCachedLoading && cachedPosts.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10 }}>
                <Loader2 style={{ width: 28, height: 28, color: '#5F6D82' }} className="animate-spin" />
                <p style={{ fontSize: 13, color: '#5F6D82' }}>Loading cached posts...</p>
              </div>
            ) : !isCachedLoading && sortedCachedPosts.length === 0 && !isRefreshing ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10 }}>
                <TrendingUp style={{ width: 36, height: 36, color: '#C8D3E2' }} />
                <p style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>No Cached Content Yet</p>
                <p style={{ fontSize: 12.5, color: '#5F6D82', textAlign: 'center', maxWidth: 300 }}>Click "Refresh" to fetch trending organic TikTok posts.</p>
              </div>
            ) : (
              <>
                {sortedCachedPosts.length > 0 && <p style={{ fontSize: 12.5, color: '#5F6D82', marginBottom: 12 }} data-testid="text-cached-count">{sortedCachedPosts.length} posts{activeCategory !== 'all' ? ` in ${CATEGORY_LABELS[activeCategory]}` : ''}</p>}
                <Masonry breakpointCols={organicMasonryBreakpoints} className="flex -ml-3 w-auto" columnClassName="pl-3 bg-clip-padding">
                  {sortedCachedPosts.map((post) => (
                    <SavedOrganicPostCard key={post.id} post={post} onDelete={() => deleteMutation.mutate(post.id)} onToggleFavorite={() => favoriteMutation.mutate(post.id)} />
                  ))}
                </Masonry>
              </>
            )}
          </>
        )}

        {activeTab === 'search' && (
          <>
            {isSearching && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10 }}>
                <Loader2 style={{ width: 28, height: 28, color: '#5F6D82' }} className="animate-spin" />
                <p style={{ fontSize: 13, color: '#5F6D82' }}>Searching TikTok for "{searchKeyword}"...</p>
                <p style={{ fontSize: 12, color: '#C8D3E2' }}>This may take 15–30 seconds</p>
              </div>
            )}
            {!isSearching && sortedSearchResults.length === 0 && !searchKeyword && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10 }}>
                <TrendingUp style={{ width: 36, height: 36, color: '#C8D3E2' }} />
                <p style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>Custom Search</p>
                <p style={{ fontSize: 12.5, color: '#5F6D82', textAlign: 'center', maxWidth: 320 }}>Search for any keyword to find trending TikTok posts.</p>
              </div>
            )}
            {!isSearching && sortedSearchResults.length > 0 && (
              <>
                <p style={{ fontSize: 12.5, color: '#5F6D82', marginBottom: 12 }}>{sortedSearchResults.length} results for "{searchKeyword}"</p>
                <Masonry breakpointCols={organicMasonryBreakpoints} className="flex -ml-3 w-auto" columnClassName="pl-3 bg-clip-padding">
                  {sortedSearchResults.map((post) => (
                    <OrganicPostCard key={post.externalId} post={post} isSaved={savedExternalIds.has(post.externalId)} onSave={() => saveMutation.mutate(post)} onClick={() => setSelectedPost(post)} />
                  ))}
                </Masonry>
              </>
            )}
          </>
        )}

        {activeTab === 'saved' && (
          sortedSavedPosts.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10 }}>
              <Bookmark style={{ width: 36, height: 36, color: '#C8D3E2' }} />
              <p style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>No Saved Posts Yet</p>
              <p style={{ fontSize: 12.5, color: '#5F6D82' }}>Search for TikTok content and save posts to reference later</p>
            </div>
          ) : (
            <Masonry breakpointCols={organicMasonryBreakpoints} className="flex -ml-3 w-auto" columnClassName="pl-3 bg-clip-padding">
              {sortedSavedPosts.map((post) => (
                <SavedOrganicPostCard key={post.id} post={post} onDelete={() => deleteMutation.mutate(post.id)} onToggleFavorite={() => favoriteMutation.mutate(post.id)} />
              ))}
            </Masonry>
          )
        )}
      </div>

      {/* Post detail modal */}
      {selectedPost && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelectedPost(null)}>
          <div style={{ background: '#F7F9FC', border: '1px solid #DCE3ED', borderRadius: 16, width: '90%', maxWidth: 480, maxHeight: '85vh', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()}>
            {selectedPost.thumbnailUrl ? (
              <img src={selectedPost.thumbnailUrl} alt="" style={{ width: '100%', maxHeight: 300, objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: 200, background: '#1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Play style={{ width: 48, height: 48, color: 'rgba(255,255,255,0.3)' }} /></div>
            )}
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {selectedPost.creatorAvatar ? <img src={selectedPost.creatorAvatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} /> : <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #5F6D82, #334155)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14, fontWeight: 700 }}>{(selectedPost.creatorName || '?')[0]?.toUpperCase()}</div>}
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', margin: 0 }}>{selectedPost.creatorName}</p>
                  {selectedPost.creatorUsername && <p style={{ fontSize: 12, color: '#5F6D82', margin: 0 }}>@{selectedPost.creatorUsername}</p>}
                </div>
              </div>
              {selectedPost.caption && <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.5, margin: 0 }}>{selectedPost.caption}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, padding: '8px 0', borderTop: '1px solid #EAF0F7' }}>
                {[{ label: 'Views', val: selectedPost.views }, { label: 'Likes', val: selectedPost.likes }, { label: 'Comments', val: selectedPost.comments }, { label: 'Shares', val: selectedPost.shares }].map(({ label, val }) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', margin: 0 }}>{formatCount(val)}</p>
                    <p style={{ fontSize: 10, color: '#5F6D82', margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button data-testid="button-save-detail" onClick={() => { saveMutation.mutate(selectedPost); setSelectedPost(null); }} disabled={savedExternalIds.has(selectedPost.externalId)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, border: 'none', background: savedExternalIds.has(selectedPost.externalId) ? '#C8D3E2' : '#1A1A1A', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: savedExternalIds.has(selectedPost.externalId) ? 'not-allowed' : 'pointer' }}>
                    <Bookmark style={{ width: 14, height: 14 }} />
                    {savedExternalIds.has(selectedPost.externalId) ? 'Already Saved' : 'Save Post'}
                  </button>
                  {selectedPost.videoUrl && (
                    <a href={selectedPost.videoUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, border: '1px solid #DCE3ED', background: '#FFFFFF', color: '#334155', fontSize: 13, fontWeight: 500, textDecoration: 'none' }}>
                      <ExternalLink style={{ width: 13, height: 13 }} />Open on TikTok
                    </a>
                  )}
                </div>
                {selectedPost.thumbnailUrl && (
                  <SaveToTemplatesButton thumbnailUrl={selectedPost.thumbnailUrl} testId="button-save-template-detail" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Assets Tab (v6.18) ───────────────────────────────────────────────────────
// Brand-folder registry over every generated creative the user owns
// (canvas_images rows — static images + video tiles). Read-only view of what
// generation already persisted to Bunny: nothing here copies or re-uploads.

interface GeneratedAsset {
  id: number;
  projectId: number;
  name: string;
  src: string;
  videoUrl: string | null;
  conceptName: string | null;
  headline: string | null;
  adStyle: string | null;
  prompt: string | null;
  wavespeedPrompt?: string | null;
  adAngle?: string | null;
  bodyCopy?: string | null;
  ctaText?: string | null;
  qaPassed?: boolean | null;
  qaReason?: string | null;
  workflowRunId?: number | null;
  originalWidth: number;
  originalHeight: number;
  createdAt: string | null;
  brandId: number | null;
  brandName: string | null;
  projectName: string | null;
}

type AssetMediaFilter = 'all' | 'image' | 'video';
type AssetSortOption = 'newest' | 'oldest';

const ASSETS_PAGE_SIZE = 40;
const assetsMasonryBreakpoints = { default: 3, 1400: 3, 1100: 2, 700: 1 };
const assetSortLabels: Record<AssetSortOption, string> = { newest: 'Newest First', oldest: 'Oldest First' };

function AssetCard({ asset, onOpen, onAdd, onClone }: { asset: GeneratedAsset; onOpen: () => void; onAdd: () => void; onClone: () => void }) {
  const { toast } = useToast();
  const { data: editorCfg } = useQuery<{ enabled: boolean }>({ queryKey: ['/api/assets/editor-enabled'] });
  const videoEditorEnabled = editorCfg?.enabled ?? false;
  const [hovered, setHovered] = useState(false);
  const [openingEditor, setOpeningEditor] = useState(false);
  // v6.20 — open the video editor on this asset. Blank window first so popup
  // blockers see the click; the session endpoint mints the /import URL.
  const handleEditVideo = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (openingEditor) return;
    setOpeningEditor(true);
    const win = window.open('', '_blank');
    try {
      const res = await fetch('/api/assets/editor-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ imageId: asset.id }),
      });
      const session = await res.json();
      if (!res.ok || !session.editorUrl) throw new Error(session.error || 'Failed to open editor');
      if (win) win.location.href = session.editorUrl;
      else window.open(session.editorUrl, '_blank');
    } catch (err) {
      win?.close();
      console.error('[assets] editor session failed:', err);
      toast({ title: 'Could not open editor', description: err instanceof Error ? err.message : undefined, variant: 'destructive' });
    } finally {
      setOpeningEditor(false);
    }
  };
  const [downloading, setDownloading] = useState(false);
  // Server-streamed: the creatives are on Bunny, which sends no CORS headers,
  // so a direct `<a download>` at the CDN URL is ignored cross-origin and
  // opens the file in a tab instead (the v4.52 canvas bug).
  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      const resp = await fetch(`/api/assets/download?id=${asset.id}`, { credentials: 'include' });
      if (!resp.ok) {
        let msg = `Download failed (${resp.status})`;
        try { msg = (await resp.json())?.error ?? msg; } catch { /* keep default */ }
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') ?? '';
      const filename = /filename="([^"]+)"/.exec(cd)?.[1] ?? `asset-${asset.id}`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      toast({ title: 'Download failed', description: err instanceof Error ? err.message : undefined, variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const title = asset.conceptName || asset.headline || asset.name;
  const created = asset.createdAt ? new Date(asset.createdAt) : null;
  const createdLabel = created
    ? created.toLocaleDateString(undefined, {
        month: 'short', day: 'numeric',
        ...(created.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' as const } : {}),
      })
    : '';
  // Date rides on the media itself now that the white footer is gone. On a
  // hovered video the mute toggle owns this corner, so it yields.
  const dateBadge = createdLabel ? (
    <span
      data-testid={`asset-date-${asset.id}`}
      style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 2, padding: '2px 7px', borderRadius: 10, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', color: '#FFFFFF', fontSize: 10.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', pointerEvents: 'none' }}
    >
      {createdLabel}
    </span>
  ) : null;

  // Canvas-style floating toolbar (same dark pill as canvas-image-toolbar).
  const hoverTools = (
    <div
      data-testid={`asset-tools-${asset.id}`}
      className="absolute z-10 flex items-center gap-0.5 bg-[#1f2937] border border-white/15 rounded-xl px-1.5 py-1.5 shadow-2xl"
      style={{ top: 8, right: 8 }}
    >
      <button data-testid={`asset-add-to-canvas-${asset.id}`} onClick={e => { e.stopPropagation(); onAdd(); }} title="Add to canvas" className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-gray-300 hover:bg-white/10 hover:text-white"><Plus className="w-3.5 h-3.5" />Add to canvas</button>
      <button data-testid={`asset-clone-${asset.id}`} onClick={e => { e.stopPropagation(); onClone(); }} disabled={!!asset.videoUrl} title={asset.videoUrl ? 'Video asset cloning is not available yet' : 'Clone'} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-gray-300 hover:bg-white/10 hover:text-white disabled:opacity-40"><Copy className="w-3.5 h-3.5" />Clone</button>
      {asset.videoUrl && videoEditorEnabled && (
        <button
          data-testid={`asset-edit-video-${asset.id}`}
          onClick={handleEditVideo}
          title="Edit video"
          className="flex items-center gap-1 px-2 py-1 rounded-lg transition-colors text-xs text-gray-300 hover:bg-white/10 hover:text-white"
        >
          {openingEditor
            ? <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" />
            : <Film className="w-3.5 h-3.5 flex-shrink-0" />}
          <span>Edit</span>
        </button>
      )}
      <button
        data-testid={`asset-download-${asset.id}`}
        onClick={handleDownload}
        title="Download"
        className="flex items-center gap-1 px-2 py-1 rounded-lg transition-colors text-xs text-gray-300 hover:bg-white/10 hover:text-white"
      >
        {downloading
          ? <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" />
          : <Download className="w-3.5 h-3.5 flex-shrink-0" />}
        <span>Download</span>
      </button>
    </div>
  );

  return (
    <div
      data-testid={`asset-card-${asset.id}`}
      role="button" tabIndex={0} aria-label={`View ${title}`}
      onClick={onOpen}
      onKeyDown={event => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onOpen(); } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ marginBottom: 12, background: '#FFFFFF', borderRadius: 12, border: '1px solid #DCE3ED', overflow: 'hidden', boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.10)' : '0 1px 4px rgba(0,0,0,0.05)', transition: 'box-shadow 150ms' }}
    >
      {asset.videoUrl ? (
        <HoverVideoCard
          videoUrl={asset.videoUrl}
          thumbnailUrl={withImpersonation(`/api/assets/thumb?id=${asset.id}&w=480`)}
          alt={title}
          testId={`asset-video-${asset.id}`}
          overlay={<>{hovered && hoverTools}{!hovered && dateBadge}</>}
        />
      ) : (
        // Natural sizing (width:100%, height:auto) — the intrinsic image ratio
        // drives the masonry cell height, same as every other grid on this page.
        // Wrapped so the hover toolbar anchors to the image, not the card.
        <div style={{ position: 'relative' }}>
          <img
            src={withImpersonation(`/api/assets/thumb?id=${asset.id}&w=480`)}
            alt={title}
            style={{ width: '100%', height: 'auto', display: 'block', aspectRatio: `${asset.originalWidth} / ${asset.originalHeight}` }}
            loading="lazy"
            onError={(e) => { const el = e.target as HTMLImageElement; el.onerror = null; el.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200"><rect width="100%" height="100%" fill="%23F0EDE8"/><text x="50%" y="50%" font-family="sans-serif" font-size="12" fill="%238C8578" text-anchor="middle">image unavailable</text></svg>'; }}
          />
          {hovered && hoverTools}
          {dateBadge}
        </div>
      )}
    </div>
  );
}

const assetActionStyle: React.CSSProperties = {display:'inline-flex',alignItems:'center',gap:6,padding:'7px 12px',borderRadius:8,border:'1px solid #DCE3ED',background:'transparent',color:'#334155',fontSize:12.5,fontWeight:500,cursor:'pointer',textDecoration:'none'};
function AssetDetailModal({asset,onClose,onAdd,onClone}:{asset:GeneratedAsset;onClose:()=>void;onAdd:()=>void;onClone:()=>void}) {
  const details: Array<[string, string | number | null | undefined]> = [
    ['Brand',asset.brandName],['Canvas',asset.projectName],['Name',asset.name],['Concept',asset.conceptName],
    ['Media',asset.videoUrl ? 'Video' : 'Image'],['Dimensions',asset.originalWidth && asset.originalHeight ? `${asset.originalWidth} × ${asset.originalHeight}` : null],
    ['Created',asset.createdAt ? new Date(asset.createdAt).toLocaleString() : null],['Style',asset.adStyle],['Angle',asset.adAngle],
    ['Headline',asset.headline],['Body copy',asset.bodyCopy],['Call to action',asset.ctaText],
    ['QA',asset.qaPassed == null ? null : asset.qaPassed ? 'Passed' : 'Needs review'],['QA details',asset.qaReason],
    ['Workflow',asset.workflowRunId],
  ];
  return <AdMediaDialog stickyFooter title={asset.conceptName || asset.headline || asset.name} imageUrl={asset.src} videoUrl={asset.videoUrl} onClose={onClose} previewTestId={`asset-preview-${asset.id}`} footer={<>
    <div style={{display:'flex',gap:8}}>
      <button onClick={onAdd} style={assetActionStyle}><Plus size={13}/>Add to canvas</button>
      <button onClick={onClone} disabled={!!asset.videoUrl} title={asset.videoUrl ? 'Video asset cloning is not available yet' : undefined} style={{...assetActionStyle,opacity:asset.videoUrl ? .45 : 1,cursor:asset.videoUrl ? 'not-allowed' : 'pointer'}}><Copy size={13}/>Clone</button>
    </div>
    <a href={withImpersonation(`/api/assets/download?id=${asset.id}`)} download style={assetActionStyle}><Download size={13}/>Download</a>
  </>}>
    <div style={{display:'grid',gridTemplateColumns:'repeat(2,minmax(0,1fr))',gap:14}}>{details.filter(([,value])=>value !== null && value !== undefined && value !== '').map(([label,value])=><div key={label}><p style={{fontSize:11,fontWeight:600,color:'#5F6D82',margin:0}}>{label}</p><p style={{fontSize:12.5,color:'#334155',margin:'4px 0 0',whiteSpace:'pre-wrap'}}>{value}</p></div>)}</div>
    {asset.prompt && <div><p style={{fontSize:11,fontWeight:600,color:'#5F6D82',margin:0}}>Saved prompt</p><p style={{fontSize:12.5,lineHeight:1.5,whiteSpace:'pre-wrap',margin:'4px 0 0'}}>{asset.prompt}</p></div>}
    {asset.wavespeedPrompt && <div><p style={{fontSize:11,fontWeight:600,color:'#5F6D82',margin:0}}>Final generation prompt</p><p style={{fontSize:12.5,lineHeight:1.5,whiteSpace:'pre-wrap',margin:'4px 0 0'}}>{asset.wavespeedPrompt}</p></div>}
    {!asset.prompt && !asset.wavespeedPrompt && <p style={{fontSize:12.5,color:'#5F6D82'}}>No saved prompt is available for this asset.</p>}
    {asset.videoUrl && <p style={{fontSize:12,color:'#5F6D82'}}>Video asset cloning is not available yet.</p>}
  </AdMediaDialog>;
}

interface AssetFolder {
  brandId: number | null;
  brandName: string;
  total: number;
  videos: number;
  images: number;
  latest: string | null;
  previews: string[];
}

/** One row in the Assets brand nav. Count reflects the active media filter,
 *  so "Nike 40" under Videos means 40 videos, not 40 assets. */
function BrandNavRow({
  label, count, active, onClick, testId,
}: { label: string; count: number; active: boolean; onClick: () => void; testId: string }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 9px', marginBottom: 1, borderRadius: 7, border: 'none',
        background: active ? '#EAF0F7' : hovered ? '#F1F5F9' : 'transparent',
        cursor: 'pointer', textAlign: 'left', transition: 'background 120ms',
      }}
    >
      <span
        style={{
          flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: active ? 600 : 500,
          color: active ? '#1A1A1A' : '#334155',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 11, color: '#5F6D82', flexShrink: 0 }}>
        {count.toLocaleString()}
      </span>
    </button>
  );
}

export function AssetsTab() {
  const [selectedAsset, setSelectedAsset] = useState<GeneratedAsset | null>(null);
  const [assetDestination, setAssetDestination] = useState<{asset: GeneratedAsset; mode: 'import-to-canvas' | 'clone-to-canvas'} | null>(null);
  const openAssetDestination = (asset: GeneratedAsset, mode: 'import-to-canvas' | 'clone-to-canvas') => {
    if (mode === 'clone-to-canvas' && asset.videoUrl) return;
    setSelectedAsset(null);
    setAssetDestination({asset,mode});
  };
  const [assets, setAssets] = useState<GeneratedAsset[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  // Which nav row is active. 'all' (the default) spans every brand; a number
  // is a brand id; 'none' is the Unfiled bucket for creatives whose canvas
  // project has no brand attached.
  const [selectedBrand, setSelectedBrand] = useState<number | 'none' | 'all'>('all');
  const [mediaFilter, setMediaFilter] = useState<AssetMediaFilter>('all');
  const [sortBy, setSortBy] = useState<AssetSortOption>('newest');
  const [mediaOpen, setMediaOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  // Monotonic token: a filter change mid-flight orphans the older response
  // instead of letting it clobber the fresh list when it lands late.
  const requestSeq = useRef(0);
  // Server offset for the next page. Advanced by rows FETCHED (not by the
  // deduped list length) so overlap re-serves under concurrent generation
  // still make forward progress instead of refetching the same window.
  const nextOffsetRef = useRef(0);

  // counts=1 skips the endpoint's preview-thumbnail pass — the nav renders
  // names and counts only.
  const {
    data: folderData,
    isLoading: foldersLoading,
    error: foldersError,
    refetch: refetchFolders,
  } = useQuery<{ folders: AssetFolder[]; total: number }>({
    queryKey: ['/api/assets/folders?counts=1'],
  });

  // Nav counts track the media filter using the per-type counts /folders
  // already returns, so switching to Videos needs no extra request. A brand
  // with nothing of the chosen type drops out rather than offering a row
  // that opens onto an empty grid.
  const countOf = useCallback(
    (f: AssetFolder) => (mediaFilter === 'image' ? f.images : mediaFilter === 'video' ? f.videos : f.total),
    [mediaFilter],
  );
  const navBrands = useMemo(() => {
    const shown = (folderData?.folders ?? []).filter((f) => countOf(f) > 0);
    // Unfiled is a catch-all, not a brand — it sinks to the bottom however
    // large it grows. Real brands keep the server's count-descending order.
    return [...shown.filter((f) => f.brandId != null), ...shown.filter((f) => f.brandId == null)];
  }, [folderData, countOf]);
  const navTotal = useMemo(() => navBrands.reduce((n, f) => n + countOf(f), 0), [navBrands, countOf]);

  // A brand can vanish from the nav when the media filter changes (e.g. an
  // image-only brand under Videos). Snap back to All instead of stranding the
  // grid on a selection with no visible row.
  useEffect(() => {
    if (selectedBrand === 'all' || !folderData) return;
    if (!navBrands.some((f) => (f.brandId ?? 'none') === selectedBrand)) setSelectedBrand('all');
  }, [navBrands, selectedBrand, folderData]);

  const selectedLabel =
    selectedBrand === 'all'
      ? 'All creatives'
      : navBrands.find((f) => (f.brandId ?? 'none') === selectedBrand)?.brandName ?? 'Creatives';

  const fetchPage = useCallback(async (offset: number, append: boolean) => {
    const seq = ++requestSeq.current;
    setIsLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(ASSETS_PAGE_SIZE));
      params.set('offset', String(offset));
      params.set('sort', sortBy);
      if (selectedBrand !== 'all') params.set('brandId', String(selectedBrand));
      if (mediaFilter !== 'all') params.set('mediaType', mediaFilter);
      const res = await fetch(`/api/assets?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Failed to load assets (${res.status})`);
      const data = await res.json();
      if (seq !== requestSeq.current) return;
      const page: GeneratedAsset[] = data.assets ?? [];
      nextOffsetRef.current = offset + page.length;
      // Offset pagination over a live table: a creative generated between
      // fetches shifts the window and re-serves the previous page's tail.
      // Dedupe by id so tiles never double (and React keys stay unique).
      setAssets((prev) => {
        if (!append) return page;
        const seen = new Set(prev.map((a) => a.id));
        return [...prev, ...page.filter((a) => !seen.has(a.id))];
      });
      setTotal(data.total ?? 0);
      setHasLoaded(true);
    } catch (err) {
      if (seq === requestSeq.current) setLoadError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      if (seq === requestSeq.current) setIsLoading(false);
    }
  }, [selectedBrand, mediaFilter, sortBy]);

  // Initial load + refetch-from-top on any brand/filter/sort change. The stale
  // total is deliberately kept until the response lands — zeroing it flashed
  // "0 assets" in the toolbar on every filter change.
  useEffect(() => {
    setAssets([]);
    nextOffsetRef.current = 0;
    fetchPage(0, false);
  }, [fetchPage]);

  const hasMore = hasLoaded && nextOffsetRef.current < total;

  // Infinite scroll: sentinel observed inside the tab's own scroll container
  // (same pattern as AdTemplatesTab — native lazy-loading is unreliable here).
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    const root = gridScrollRef.current;
    if (!sentinel || !root || !hasMore || isLoading || loadError) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) fetchPage(nextOffsetRef.current, true);
    }, { root, rootMargin: '800px' });
    io.observe(sentinel);
    return () => io.disconnect();
  }, [assets.length, hasMore, isLoading, loadError, fetchPage]);

  const dropdownRowStyle: React.CSSProperties = { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', fontSize: 12.5, color: '#334155', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' };
  const rowHover = {
    onMouseEnter: (e: React.MouseEvent) => ((e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9'),
    onMouseLeave: (e: React.MouseEvent) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent'),
  };

  return (
    <div data-testid="assets-tab" style={{ height: '100%', display: 'flex', minHeight: 0 }}>
      {/* ── Brand nav ─────────────────────────────────────────────────── */}
      {selectedAsset && <AssetDetailModal asset={selectedAsset} onClose={() => setSelectedAsset(null)} onAdd={() => openAssetDestination(selectedAsset,'import-to-canvas')} onClone={() => openAssetDestination(selectedAsset,'clone-to-canvas')} />}
      {assetDestination && <CloneDestinationPicker imageUrl={assetDestination.asset.src} imageName={assetDestination.asset.conceptName || assetDestination.asset.name} mode={assetDestination.mode} onClose={() => setAssetDestination(null)}
        {...(assetDestination.mode === 'import-to-canvas' ? {onPicked: async (projectId: number) => {
          const asset = assetDestination.asset;
          await importDiscoveryImages(projectId,[{...asset,imageUrl:asset.src}]);
          const store = useCanvasStore.getState();
          await store.switchProject(projectId);
          await store.refreshProjectImages(projectId);
          const imported = useCanvasStore.getState().projects[projectId]?.images.find(image => image.src === asset.src);
          if (imported) {
            store.setSelectedImageIds([imported.id]);
            store._writeProjectView(projectId,{pendingFitToRow:{minX:imported.x,minY:imported.y,maxX:imported.x+imported.displayWidth,maxY:imported.y+imported.displayHeight}});
          }
          store.setCanvasOpen(true);
          store.setActiveView('chat-canvas');
        }} : {})}
      />}
      <div
        data-testid="assets-nav"
        style={{ width: 210, flexShrink: 0, background: '#FFFFFF', borderRight: '1px solid #DCE3ED', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <div style={{ padding: '12px 14px 7px', fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: '#5F6D82', flexShrink: 0 }}>
          Brands
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 12px' }}>
          {foldersLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} style={{ height: 15, margin: '9px 9px', borderRadius: 4, background: '#EAF0F7', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
            ))
          ) : foldersError ? (
            <div style={{ padding: '10px 9px', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 12, color: '#5F6D82' }}>Couldn't load brands</span>
              <button
                data-testid="button-brands-retry"
                onClick={() => refetchFolders()}
                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#FFFFFF', color: '#334155', fontSize: 11.5, fontWeight: 500, cursor: 'pointer' }}
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              <BrandNavRow
                testId="assets-brand-all"
                label="All"
                count={navTotal}
                active={selectedBrand === 'all'}
                onClick={() => setSelectedBrand('all')}
              />
              {navBrands.length > 0 && (
                <div style={{ height: 1, background: '#EAF0F7', margin: '6px 9px' }} />
              )}
              {navBrands.map((f) => {
                const id = f.brandId ?? ('none' as const);
                return (
                  <BrandNavRow
                    key={String(id)}
                    testId={`assets-brand-${id}`}
                    label={f.brandName}
                    count={countOf(f)}
                    active={selectedBrand === id}
                    onClick={() => setSelectedBrand(id)}
                  />
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* ── Grid column ───────────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <div style={{ background: '#FFFFFF', borderBottom: '1px solid #DCE3ED', padding: '10px 20px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span data-testid="text-assets-scope" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>
              <Folder style={{ width: 13, height: 13, color: '#5F6D82' }} />
              {selectedLabel}
            </span>

            {/* Media type */}
            <div style={{ position: 'relative' }}>
              <button data-testid="button-assets-media-type" onClick={() => setMediaOpen(!mediaOpen)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 12, color: '#334155', cursor: 'pointer' }}>
                {mediaFilter === 'video' ? <Video style={{ width: 11, height: 11 }} /> : <Image style={{ width: 11, height: 11 }} />}
                {mediaFilter === 'image' ? 'Images' : mediaFilter === 'video' ? 'Videos' : 'All Media'}
              </button>
              {mediaOpen && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setMediaOpen(false)} />
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#FFFFFF', border: '1px solid #DCE3ED', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, padding: '4px 0', minWidth: 150 }}>
                    {([['all', 'All Media'], ['image', 'Images'], ['video', 'Videos']] as [AssetMediaFilter, string][]).map(([val, label]) => (
                      <button key={val} data-testid={`assets-media-${val}`} onClick={() => { setMediaFilter(val); setMediaOpen(false); }} style={dropdownRowStyle} {...rowHover}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          {val === 'video' ? <Video style={{ width: 11, height: 11, color: '#5F6D82' }} /> : val === 'image' ? <Image style={{ width: 11, height: 11, color: '#5F6D82' }} /> : null}
                          {label}
                        </span>
                        {mediaFilter === val && <Check style={{ width: 11, height: 11, color: '#1A1A1A' }} />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Date sort */}
            <div style={{ position: 'relative' }}>
              <button data-testid="button-assets-sort" onClick={() => setSortOpen(!sortOpen)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6, border: '1px solid #DCE3ED', background: '#F7F9FC', fontSize: 12, color: '#334155', cursor: 'pointer' }}>
                <ArrowUpDown style={{ width: 11, height: 11 }} />{assetSortLabels[sortBy]}
              </button>
              {sortOpen && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setSortOpen(false)} />
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#FFFFFF', border: '1px solid #DCE3ED', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, padding: '4px 0', minWidth: 150 }}>
                    {(Object.entries(assetSortLabels) as [AssetSortOption, string][]).map(([opt, label]) => (
                      <button key={opt} data-testid={`assets-sort-${opt}`} onClick={() => { setSortBy(opt); setSortOpen(false); }} style={dropdownRowStyle} {...rowHover}>
                        {label}
                        {sortBy === opt && <Check style={{ width: 11, height: 11, color: '#1A1A1A' }} />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {hasLoaded && !loadError && (
            <span data-testid="text-assets-count" style={{ fontSize: 12, color: '#5F6D82' }}>
              {total.toLocaleString()} {total === 1 ? 'creative' : 'creatives'}
            </span>
          )}
        </div>

        {/* Results */}
        <div ref={gridScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: 'transparent' }}>
          {isLoading && assets.length === 0 ? (
            <Masonry breakpointCols={assetsMasonryBreakpoints} className="flex -ml-3 w-auto" columnClassName="pl-3 bg-clip-padding">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ marginBottom: 12, background: '#FFFFFF', borderRadius: 12, border: '1px solid #DCE3ED', overflow: 'hidden', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
                  <div style={{ height: i % 3 === 0 ? 220 : i % 3 === 1 ? 300 : 180, background: '#DCE3ED' }} />
                  <div style={{ padding: '8px 10px' }}><div style={{ height: 8, background: '#DCE3ED', borderRadius: 4, width: '50%' }} /></div>
                </div>
              ))}
            </Masonry>
          ) : loadError && assets.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12, textAlign: 'center' }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X style={{ width: 20, height: 20, color: '#DC2626' }} /></div>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>Couldn't load creatives</p>
              <p style={{ fontSize: 12.5, color: '#5F6D82' }}>{loadError}</p>
              <button onClick={() => fetchPage(0, false)} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #DCE3ED', background: '#FFFFFF', color: '#334155', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }}>Try Again</button>
            </div>
          ) : assets.length === 0 && hasLoaded ? (
            <div className="workspace-empty-state" data-testid="saved-creatives-empty">
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#EAF0F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Image style={{ width: 20, height: 20, color: '#5F6D82' }} /></div>
              <h2>{mediaFilter !== 'all' ? 'No matching creatives' : 'Your next creative belongs here.'}</h2>
              <p style={{ fontSize: 12.5, color: '#5F6D82' }}>
                {mediaFilter !== 'all'
                  ? 'Try another media type to see your saved creatives.'
                  : selectedBrand === 'all'
                    ? 'Create your first ad. Your generated creatives will appear here automatically.'
                    : 'Creatives you generate for this brand appear here automatically.'}
              </p>
              {mediaFilter !== 'all' ? (
                <button className="workspace-primary-action" onClick={() => setMediaFilter('all')}>Show all media</button>
              ) : (
                <Link href="/canvas" className="workspace-primary-action">Create an ad</Link>
              )}
            </div>
          ) : (
            <>
              <Masonry breakpointCols={assetsMasonryBreakpoints} className="flex -ml-3 w-auto" columnClassName="pl-3 bg-clip-padding">
                {assets.map((asset) => (
                  <AssetCard key={asset.id} asset={asset} onOpen={() => setSelectedAsset(asset)} onAdd={() => openAssetDestination(asset,'import-to-canvas')} onClone={() => openAssetDestination(asset,'clone-to-canvas')} />
                ))}
              </Masonry>
              {loadError && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '16px 0' }}>
                  <span style={{ fontSize: 12.5, color: '#5F6D82' }}>{loadError}</span>
                  <button onClick={() => fetchPage(nextOffsetRef.current, true)} style={{ padding: '5px 14px', borderRadius: 8, border: '1px solid #DCE3ED', background: '#FFFFFF', color: '#334155', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Retry</button>
                </div>
              )}
              {hasMore && !loadError && <div ref={loadMoreRef} aria-hidden style={{ height: 1 }} />}
              {isLoading && assets.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                  <Loader2 style={{ width: 16, height: 16, color: '#5F6D82' }} className="animate-spin" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Unified View ────────────────────────────────────────────────────────
export function CanvasAdLibraryView() {
  const [activeTab, setActiveTab] = useState<AdLibTab>('ad-templates');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showZipModal, setShowZipModal] = useState(false);
  const queryClient = useQueryClient();
  // Discover handoff: snapshot the pre-filter on mount, then clear it from the
  // store so re-opening the Ad Library from the sidebar starts clean.
  const [initialStyleFilters] = useState<string[] | undefined>(
    () => useCanvasStore.getState().adLibraryInitialStyles ?? undefined,
  );
  const [initialFocusId] = useState<number | undefined>(
    () => useCanvasStore.getState().adLibraryFocusId ?? undefined,
  );
  useEffect(() => {
    const s = useCanvasStore.getState();
    if (s.adLibraryInitialStyles || s.adLibraryFocusId != null) {
      useCanvasStore.setState({ adLibraryInitialStyles: null, adLibraryFocusId: null });
    }
  }, []);

  const { data: templates = [] } = useQuery<AdTemplate[]>({ queryKey: ['/api/ad-templates'] });
  const { data: savedPosts = [] } = useQuery<OrganicPost[]>({ queryKey: ['/api/organic-posts'] });

  const tabConfig: { id: AdLibTab; label: string; count?: number }[] = [
    { id: 'ad-templates', label: 'Ad templates', count: templates.length },
    { id: 'competitors', label: 'Competitors' },
    { id: 'my-ads', label: 'My Ads' },
    { id: 'find-ads', label: 'Find ads' },
    // v2.31 — "Organic posts" tab hidden for now (not needed). The tab, its
    // OrganicPostsTab component, and routes stay in the code — re-add this
    // entry to bring it back.
    // { id: 'organic-posts', label: 'Organic posts', count: savedPosts.length },
  ];

  return (
    <div data-testid="canvas-ad-library-view" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'transparent', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #DCE3ED', padding: '0 20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '16px 0 0' }}>
          <div><h1 className="workspace-page-title">Inspiration</h1><p className="workspace-page-description">Explore ad templates and references for your next creative.</p></div>
          {activeTab === 'ad-templates' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button data-testid="button-canvas-upload-zip" onClick={() => setShowZipModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, border: '1px solid #DCE3ED', background: '#FFFFFF', color: '#334155', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }} onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9')} onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FFFFFF')}>
                <FolderArchive style={{ width: 13, height: 13 }} />Upload ZIP
              </button>
              <button data-testid="button-canvas-upload-template" onClick={() => setShowUploadModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, border: 'none', background: '#1A1A1A', color: '#FFFFFF', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <Upload style={{ width: 13, height: 13 }} />Upload
              </button>
            </div>
          )}
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginTop: 10, overflowX: 'auto' }}>
          {tabConfig.map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px 9px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #1A1A1A' : '2px solid transparent',
                color: activeTab === tab.id ? '#1A1A1A' : '#5F6D82',
                fontSize: 13.5,
                fontWeight: activeTab === tab.id ? 600 : 400,
                cursor: 'pointer',
                transition: 'color 120ms, border-color 120ms',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => { if (activeTab !== tab.id) (e.currentTarget as HTMLButtonElement).style.color = '#334155'; }}
              onMouseLeave={(e) => { if (activeTab !== tab.id) (e.currentTarget as HTMLButtonElement).style.color = '#5F6D82'; }}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 20, padding: '1px 6px', borderRadius: 20, background: activeTab === tab.id ? '#1A1A1A' : '#EAF0F7', color: activeTab === tab.id ? '#FFFFFF' : '#526074', fontSize: 11, fontWeight: 600 }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {activeTab === 'ad-templates' && <AdTemplatesTab onShowUpload={() => setShowUploadModal(true)} onShowZip={() => setShowZipModal(true)} initialStyleFilters={initialStyleFilters} initialFocusId={initialFocusId} />}
        {activeTab === 'my-ads' && <AdTemplatesTab key="my-ads" myAds onShowUpload={() => {}} onShowZip={() => {}} />}
        {activeTab === 'competitors' && <AdTemplatesTab key="competitors" competitors onShowUpload={() => {}} onShowZip={() => {}} />}
        {activeTab === 'find-ads' && <FindAdsTab />}
        {activeTab === 'organic-posts' && <OrganicPostsTab />}
      </div>

      {showUploadModal && (
        <UploadModal onClose={() => setShowUploadModal(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/ad-templates'] })} />
      )}
      {showZipModal && (
        <ZipUploadModal onClose={() => setShowZipModal(false)} />
      )}
    </div>
  );
}
